import React, { createContext, useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { TimePicker } from '@progress/kendo-react-dateinputs';
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Dialog } from "@progress/kendo-react-dialogs";
import moment from 'moment';
import {
  createDateTimeInUserTimeZone,
  getUserDayBoundsForInstant,
  toUserEndOfDayISOString,
  toUserStartOfDayISOString,
} from '../utils/timeZone';

import { getRecentMatters, searchObjects, updateTimeEntries, updateTimeSlot, createTimeSlots, getTimeSlots } from '../../api/commonApi';
import { getItemSourceIcons, getUnavailableTimeMessage, sourceIconMapping, sourceNameMapping, isRunningClocking, isActuallyRunningClocking, isClockingItem, isFinalizedClocking, isReminderCalendarItem, isEmailActivityItem, SOURCE_CLOCKING_STATIC_ICON, SOURCE_CLOCKING_ICON, SOURCE_CLOCKING_RUNNING_ICON } from "../components/common/sourceIcons";
import { WebActivityNotes } from "../components/common/WebActivityNotes";
import { shouldUseWebActivityLabel } from "../components/common/webActivityQuery";
import { createPortal } from "react-dom";
import { useWebpush } from "./Notification/WebpushContext";
import { useNotification } from "./Notification/NotificationContext";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Skeleton } from '@progress/kendo-react-indicators';

import infoIcon from '../../public/assets/information.svg';
import HYPER_LINK from '../../public/assets/hyperlink_open.svg';
import EDIT_ICON from "../../public/assets/common/icon_editable_gray.svg";
import CARD_QUESTION_RED from "../../public/assets/card_question_red.svg";
import {
  hasUnavailableSlotDuration,
  isNoiseEntry,
  isNoiseItem,
} from "../components/common/timeNoiseUtils";
import { NetDocumentsNoiseDurationHint } from "../components/common/NetDocumentsNoiseDurationHint";
import { RenderDeleteSlot } from '@features/timeSlots/components/action/RenderDeleteSlot';
import { HighlightedText } from '@features/timelineSidebar/components/filters/HighlightedText';
import { useClockingStore } from '@/stores/useClockingStore';
import { useTimeEntriesStore } from '@/stores/useTimeEntriesStore';
import { getCreatedClockingSlotId } from '@features/clocking/utils/getCreatedClockingSlot';
import ClockingTimer from '@features/clocking/components/controls/ClockingTimer';
import { HighlightText } from './HighlightText';

export const DurationSavingSkeleton = () => (
  <span
    role="status"
    aria-label="Updating duration"
    className="ml-1 inline-flex shrink-0 items-center"
  >
    <Skeleton
      style={{ width: 38, height: 14, borderRadius: 3 }}
      animation={{ type: 'wave' }}
    />
  </span>
);

export const HintIcon = ({
  hintMsg,
  position = "top",
  icon = infoIcon,   // 新增：外部可传 icon，不传就用默认的
  iconWidth,         // 可选：图标宽度
  iconHeight,        // 可选：图标高度
  maxTooltipWidth = "264px",
  openDelay = /** @type {number | undefined} */ (undefined),
}) => {
  return (
    <Tooltip
      tooltipStyle={{
        maxWidth: maxTooltipWidth,
        border: "none",
        backgroundColor: "#000",
        color: "#fff",
        padding: "4px 8px",
        fontSize: "14px",
        fontWeight: 400,
        zIndex: 999999,
      }}
      anchorElement="target"
      position={position}
      openDelay={openDelay}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <img
          title={hintMsg}
          src={icon}    // 使用传入的 icon
          style={{
            ...(iconWidth ? { width: iconWidth } : {}),
            ...(iconHeight ? { height: iconHeight } : {}),
          }}
          alt=""
        />
      </div>
    </Tooltip>
  );
};

export const HintAvatar = ({
  avatar,
  hintMsg,
  width = '264px',
  children,
}) => {

  const avatarNode = (
    <img
      title={hintMsg}
      src={avatar}
      alt=""
      style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
      }}
    />
  );

  return (
    <Tooltip
      tooltipStyle={{
        maxWidth: width,
        border: 'none',
        backgroundColor: '#000',
        color: '#fff',
        padding: '4px 8px',
        fontSize: '14px',
        fontWeight: 400,
      }}
      position="top"
      anchorElement="target"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
        {avatarNode}
        {children}
      </span>
    </Tooltip>
  );
};

export const HoverHintIcon = ({ hintMsg, position = "right", normalIcon, hoverIcon }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Tooltip
      tooltipStyle={{
        maxWidth: "264px",
        border: "none",
        backgroundColor: "#000",
        color: "#fff",
        padding: "4px 8px",
        fontSize: "14px",
        fontWeight: 400,
      }}
      anchorElement="target"
      position={position}
    >
      <div
        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={isHovered ? hoverIcon : normalIcon}
          alt=""
          title={hintMsg}
        />
      </div>
    </Tooltip>
  );
};

export const ConvertSecondsToHours = (seconds) => {

  if (seconds === 0) {
    return 0;
  }

  const hours = seconds / 3600;
  const ceiledHours = Math.ceil(hours * 10) / 10;
  return ceiledHours.toFixed(1);
};

/**
 * 计算总小时数，避免浮点误差
 * @param {Array} items - 每项包含 duration（秒）
 * @param {number} minMinutes - 用于控制小数位
 * @returns {number} totalHours
 */
export const getTotalHours = (items, minMinutes) => {
  // items 若不是数组，强制变成空数组
  const list = Array.isArray(items) ? items : [];

  // 根据 minMinutes 决定小数位
  let fractionDigits = 2;
  if (typeof minMinutes === 'number') {
    if (minMinutes >= 15) fractionDigits = 0;
    else if (minMinutes >= 6) fractionDigits = 1;
  }

  const factor = Math.pow(10, fractionDigits);

  // 累加整数小时（放大 factor 避免浮点误差）
  const totalInt = list.reduce((sum, i) => {
    const hours = (i?.duration || 0) / 3600;
    return sum + Math.round(hours * factor);
  }, 0);

  return totalInt / factor;
};





export const ConvertCodeToObj = (code, options) => {
  const find = (Array.isArray(options) ? options : []).find(option => option.code === code)
  if (find) {
    return find
  } else {
    return {
      "name": code,
      "code": code,
    }
  }
}

export const listenForOutsideClick = (listening, setListening, menuRef, callBack) => {
  return () => {
    if (listening) return;
    if (!menuRef.current) return;
    setListening(true);
    [`click`, `touchstart`].forEach((type) => {
      document.addEventListener(type, (evt) => {
        // 🛡 防御性判断
        if (
          !menuRef.current 
          || !(menuRef.current instanceof HTMLElement)
          || !(evt.target instanceof Node)
        ) return;
        // 假设你要匹配的 class 列表
        const classListToCheck = [
          'k-i-x',
          'k-list-item-text',
          'k-list-item',
          'popup-click',
          'custom-floating-window',
          'k-window',
          'k-window-titlebar',
          'k-window-actions',
          'k-window-content',
          'k-pdf-viewer',
          'k-pdf-viewer-pages',
          'k-pdf-viewer-page',
          'narrative-ocg-dialog-backdrop',
          'narrative-ocg-details-dialog',
          'narrative-ocg-reference-window'
        ];

        // 取点击的 HTMLElement
        let el = evt.target instanceof HTMLElement ? evt.target : evt.target.parentNode;
        if (!el || !(el instanceof HTMLElement)) return;

        const eventPath = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
        const clickedInsidePath = eventPath.some(node => (
          node instanceof HTMLElement
          && (
            menuRef.current.contains(node)
            || classListToCheck.some(cls => node.classList?.contains(cls) || node.closest?.(`.${cls}`))
          )
        ));

        if (clickedInsidePath) return;

        // 判断点击节点或其父元素链是否有匹配的 class
        const clickedInsideSpecial = classListToCheck.some(cls => el.closest(`.${cls}`));

        if (menuRef.current.contains(el) || clickedInsideSpecial) return;

        // 点击在 menu 外部，触发回调
        callBack();
      });
    });
  }
}

/**
 * 将后端列表转换为前端可用的 label/value 格式
 * 支持 label 多字段拼接
 * @param {Array} rawList 原始数组
 * @param {Object} config 可配置项 { labelFields: ["name"], value: "code", separator: " - " }
 * @returns {Array} 转换后的 [{ label, value, raw }]
 */
export function transformOptions(rawList, config = { labelFields: ["name"], value: "code", separator: " - " }) {
  return rawList.map(item => {
    const label = config.labelFields
      .map(field => item[field])
      .filter(Boolean)
      .join(config.separator || " ");

    return {
      ...item,
      label,
      value: item[config.value],
      raw: item
    };
  });
}

// withTooltipItemRender.js

export const withTooltipItemRender = (config = {}) => {
  const {
    field = "label",          // 默认展示字段
    position = "right",       // Tooltip 显示位置
    maxWidth = "300px",       // 下拉项最大宽度
    tooltipProps = {},        // 额外 Tooltip 配置
  } = config;

  return (li, itemProps) => {
    const content = itemProps.dataItem?.[field] || "";

    return (
      <Tooltip
        anchorElement="element"
        position={position}
        {...tooltipProps}
      >
        <div title={content}>
          {React.cloneElement(li, {
            ...li.props,
            style: {
              ...li.props.style,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth,
            },
          })}
        </div>
      </Tooltip>
    );
  };
};


/**
 * 创建一个支持异步加载、过期控制和并发合并的缓存。
 */
export function createAsyncCache({ ttl = 60000, maxSize = 100, onError } = {}) {
  const cache = new Map();   // key -> { value, timestamp }
  const pending = new Map(); // key -> pending promise

  function isExpired(entry) {
    return Date.now() - entry.timestamp > ttl;
  }

  return {
    /**
     * 获取缓存数据，如果不存在或过期则通过 asyncFetcher 加载。
     * @param {any} key
     * @param {(key) => Promise<any>} asyncFetcher
     * @param {any} fallbackValue
     */
    async get(key, asyncFetcher, fallbackValue) {
      const cached = cache.get(key);
      if (cached && !isExpired(cached)) {
        return cached.value;
      }

      if (pending.has(key)) {
        return pending.get(key);
      }

      if (typeof asyncFetcher !== 'function') {
        if (fallbackValue !== undefined) return fallbackValue;
        throw new TypeError(`No valid fetcher provided for key: ${key}`);
      }

      const promise = asyncFetcher(key)
        .then((value) => {
          cache.set(key, { value, timestamp: Date.now() });

          if (cache.size > maxSize) {
            const firstKey = cache.keys().next().value;
            cache.delete(firstKey);
          }

          pending.delete(key);
          return value;
        })
        .catch((err) => {
          pending.delete(key);
          if (onError) onError(err, key);
          if (fallbackValue !== undefined) return fallbackValue;
          throw err;
        });

      pending.set(key, promise);
      return promise;
    },

    /**
     * 仅从缓存中获取值（不触发异步加载）
     * @param {any} key
     * @returns {any | null}
     */
    safeGet(key) {
      const cached = cache.get(key);
      return cached && !isExpired(cached) ? cached.value : null;
    },

    /**
     * 判断是否存在有效缓存
     * @param {any} key
     */
    has(key) {
      const cached = cache.get(key);
      return cached && !isExpired(cached);
    },

    /**
     * 手动设置缓存
     * @param {any} key
     * @param {any} value
     */
    set(key, value) {
      cache.set(key, { value, timestamp: Date.now() });
    },

    /**
     * 删除指定 key
     */
    delete(key) {
      cache.delete(key);
      pending.delete(key);
    },

    /**
     * 清空所有缓存和挂起请求
     */
    clear() {
      cache.clear();
      pending.clear();
    },

    /**
     * 获取所有缓存的 key
     */
    keys() {
      return Array.from(cache.keys());
    },

    /**
     * 当前缓存大小
     */
    size() {
      return cache.size;
    },

    /**
     * 清除所有过期的缓存项
     */
    clearExpired() {
      for (const [key, entry] of cache.entries()) {
        if (isExpired(entry)) {
          cache.delete(key);
        }
      }
    }
  };
}

// 格式化 Solr 关键词，避免特殊字符干扰（假设你已有）
export function formatSolrKeyword(input) {
  if (input == null) return "*";
  const str = String(input).trim();
  if (!str) return "*";
  return str.replace(/([+\-!(){}\[\]^"~*?:\\\/ ])/g, "\\$1");
}

export function roundDurationToNearestUnitInSeconds(durationSeconds, minUnitMinutes) {
  // if (!durationSeconds || durationSeconds <= 0 || !minUnitMinutes) return 0;

  // const durationMinutes = durationSeconds / 60;
  // const roundedMinutes = Math.ceil(durationMinutes / minUnitMinutes) * minUnitMinutes;
  // return roundedMinutes * 60;
  return durationSeconds;
}

/**
 * 将小时数按指定最小单位向上取整
 * @param {number} val - 原始小时数
 * @param {number} minUnit - 最小单位（小时），例如 0.1 或 0.01
 * @returns {number} - 向上取整后的小时数
 */
export const roundDuration = (val, minMinutes) => {
  if (val == null || isNaN(val)) return null;

  const precision = minMinutes ? 1 : 2;
  const minValue = minMinutes ? 0.1 : 0.01;

  const factor = Math.pow(10, precision);
  let rounded = Math.ceil(val * factor) / factor;

  // 保证最小值（但允许 0 保持 0）
  if (val > 0 && rounded < minValue) {
    rounded = minValue;
  }
  return rounded;
};

const parseDurationStart = (start) => {
  if (!start) return null;

  if (Array.isArray(start)) {
    for (const candidate of start) {
      const parsedCandidate = parseDurationStart(candidate);
      if (parsedCandidate && !isNaN(parsedCandidate)) return parsedCandidate;
    }

    return null;
  }

  if (start instanceof Date) return start;

  if (typeof start === "string") {
    const parsedDate = new Date(start);
    if (!isNaN(parsedDate)) return parsedDate;

    const timeMatch = start.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) {
      const [, hours, minutes, seconds = "0"] = timeMatch;
      return createDateTimeInUserTimeZone(
        moment().format("YYYY-MM-DD"),
        `${hours}:${minutes}:${seconds}`,
      );
    }
  }

  return new Date(start);
};

export const getMaxDurationSecondsForStart = (start) => {
  const startDate = parseDurationStart(start);
  if (!startDate || isNaN(startDate)) return 24 * 3600;

  const { end: endOfStartDay } = getUserDayBoundsForInstant(startDate);
  if (!endOfStartDay) return 24 * 3600;

  return Math.max(0, Math.floor((endOfStartDay.getTime() - startDate.getTime()) / 1000));
};

export const getMaxDurationHoursForStart = (start, minMinutes) => {
  const precision = minMinutes ? 1 : 2;
  const factor = Math.pow(10, precision);
  const maxHours = getMaxDurationSecondsForStart(start) / 3600;

  return Math.floor(maxHours * factor) / factor;
};

export const clampDurationHoursToStartDay = (durationHours, start, minMinutes) => {
  if (durationHours == null || isNaN(durationHours)) return null;

  return Math.max(0, Math.min(durationHours, getMaxDurationHoursForStart(start, minMinutes)));
};

export const clampDurationSecondsToStartDay = (durationSeconds, start) => {
  if (durationSeconds == null || isNaN(durationSeconds)) return 0;

  return Math.max(0, Math.min(Math.round(durationSeconds), getMaxDurationSecondsForStart(start)));
};

export const getMinIncrementSeconds = (minMinutes) => {
  const minutes = Number(minMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return 0;
  return Math.round(minutes * 60);
};

const formatSyncedNumericTextBoxValue = (value, precision = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";

  if (!precision) return String(Math.round(numericValue));

  return numericValue
    .toFixed(precision)
    .replace(/(\.\d*?)0+$/, "$1")
    .replace(/\.$/, "");
};

export const useSyncClampedNumericTextBoxValue = (numericTextBoxRef, value, precision = 2) => {
  useLayoutEffect(() => {
    const input = numericTextBoxRef.current?.element;
    const numericValue = Number(value);
    if (!input || !Number.isFinite(numericValue)) return;

    const currentValue = Number(String(input.value).replace(/,/g, ""));
    if (!Number.isFinite(currentValue) || currentValue <= numericValue) return;

    const nextValue = formatSyncedNumericTextBoxValue(numericValue, precision);
    if (input.value === nextValue) return;

    input.value = nextValue;

    if (typeof input.setSelectionRange === "function") {
      const cursorPosition = nextValue.length;
      input.setSelectionRange(cursorPosition, cursorPosition);
    }
  });
};

export function getExportFileName(prefix = "Time Entries") {
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, '0');

  const month = pad(now.getMonth() + 1);
  const day = pad(now.getDate());
  const year = now.getFullYear();
  const timestamp = now.getTime(); // 毫秒级时间戳

  return `${prefix}_${month}_${day}_${year}_${timestamp}.xlsx`;
}

export const getEarliestDateStr = (items, fields) => {
  const dates = items
    .map(item => {
      for (const field of fields) {
        const value = item[field];
        if (value) {
          const date = new Date(value);
          if (!isNaN(date)) return date;
        }
      }
      return null;
    })
    .filter(d => d); // 过滤掉 null 或无效日期

  if (dates.length === 0) return '';

  dates.sort((a, b) => a - b);
  return dates[0].toISOString();
};

export const getFirstNonEmptyField = (field, items) => {
  const found = items.find(c => c[field] !== null && c[field] !== undefined && c[field] !== '');
  return found ? found[field] : null;
};

export const destinationStatus = (item) => {
  // TODO, 改讀配置
  switch (item) {
    case 0:
      return 'Saved';
    case 1:
      return 'Pending';
    default:
      return '';

  }
};

export const miraStatus = (item) => {
  switch (item) {
    case 2:
      return 'discarded';
    case 0:
    case 1:
    case 4:
    default:
      return 'ready2release';

  }
};

export const getDateRange = (filter, step = 0, selectedWeekDay = null) => {
  const now = moment(new Date());

  // 如果 selectedWeekDay 为空，则默认今天
  if (!selectedWeekDay) {
    selectedWeekDay = { date: now };
  }

  let startDate, endDate;

  switch (filter) {
    case 1:
      startDate = now.clone().add(step, 'months').startOf('month');
      endDate = now.clone().add(step, 'months').endOf('month');
      break;
    case 2:
      startDate = now.clone().add(step, 'weeks').startOf('week');
      endDate = now.clone().add(step, 'weeks').endOf('week');
      break;
    case 3:
      // selectedWeekDay 是日期字符串或 moment 对象
      startDate = moment(selectedWeekDay.date);
      endDate = moment(selectedWeekDay.date);
      break;
    default:
      startDate = now;
      endDate = now;
  }

  return {
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD')
  };
};

export const formatDurationParts = (hours) => {
    const parts = [];

    if (hours <= 0 || isNaN(hours)) return [{ value: 0, unit: 's' }];

    const totalSeconds = Math.round(hours * 3600);

    const hrs = Math.floor(totalSeconds / 3600);
    const remainingSeconds = totalSeconds % 3600;
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;

    // Show the full h/m/s breakdown, omitting any unit that is zero - except
    // seconds, which is always shown when it's the only non-zero unit.
    if (hrs > 0) parts.push({ value: hrs, unit: 'h' });
    if (mins > 0) parts.push({ value: mins, unit: 'm' });
    if (secs > 0 || parts.length === 0) parts.push({ value: secs, unit: 's' });

    return parts;
};

export const formatSecondsToDuration = (seconds) =>
    formatDurationParts((seconds || 0) / 3600).map(p => `${p.value}${p.unit}`).join(' ');

export const formatSecondsToTenthsHours = (seconds, { lessThanOneTenthLabel = null } = {}) => {
    const totalSeconds = Number(seconds) || 0;

    if (lessThanOneTenthLabel && totalSeconds > 0 && totalSeconds < 360) {
        return `${lessThanOneTenthLabel} h`;
    }

    const ceiledHours = totalSeconds > 0
        ? Math.ceil((totalSeconds / 3600) * 10) / 10
        : 0;
    const value = Number.isInteger(ceiledHours)
        ? String(ceiledHours)
        : ceiledHours.toFixed(1);

    return `${value} h`;
};



export const toLocalStartOfDay = (dateString) => {
  return toUserStartOfDayISOString(dateString);
}

export const toLocalEndOfDay = (dateString) => {
  return toUserEndOfDayISOString(dateString);
}

export const fetchRecent = async (hasDestination, delegatorId) => {
  try {
    if (!hasDestination) return [];
    
    const recentRaw = await getRecentMatters(delegatorId);

    if (!recentRaw || !recentRaw.length) return [];

    const idOrderMap = new Map();
    recentRaw.forEach((item, index) => {
      if (item.matter_id) idOrderMap.set(String(item.matter_id), index);
    });

    const matterColors = ["#EBEBEB", "#EBEBEB", "#EBEBEB", "#EBEBEB", "#EBEBEB"];

    const ids = recentRaw.filter(r => r.matter_id).map(r => r.matter_id);
    const numbers = recentRaw.filter(r => !r.matter_id && r.matter_number).map(r => '(*' + formatSolrKeyword(r.matter_number) + '*)');

    let filterParts = [];
    if (ids.length > 0) filterParts.push(`obj_id:(${ids.join(" OR ")})`);
    if (numbers.length > 0) filterParts.push(`number:(${numbers.join(" OR ")})`);

    const filterString = '(' + filterParts.join(" OR ") + ')';

    const fullData = await searchObjects(
      "matter",
      "",
      0,
      200, 
      "obj_id,name,number,client_id,client_number,client_name,is_billable,type,utbms_required",
      filterString,
      hasDestination
    );

    const sortedData = fullData
      .filter(Boolean)
      .sort((a, b) => {
        const orderA = idOrderMap.get(String(a.obj_id)) ?? 999;
        const orderB = idOrderMap.get(String(b.obj_id)) ?? 999;
        return orderA - orderB;
      });
    
    return sortedData
      .slice(0, 10) 
      .map((matter, index) => ({
        ...matter,
        color: matterColors[index % matterColors.length],
      }));
      
  } catch (err) {
    console.error("fetchRecent error", err);
    return [];
  }
};

export const RenderNarrative = (text, metadata, cell, item, highlightQuery = "") => {

  const variableKeys = getNarrativeMapping(item);
  const elements = [];

  const narrativeVars = Array.isArray(metadata?.narrativeVariables)
    ? metadata.narrativeVariables
    : [];
  if (!text) return null;
  const metaMap = {};
  narrativeVars.forEach(v => {
    if (v.value) metaMap[v.value] = v.placeholder;
  });

  const regexParts = [...Object.keys(variableKeys), ...Object.keys(metaMap)]
    .map(s => s.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&"));

  let regex = null;
  if (regexParts.length > 0) {
    regex = new RegExp(`(${regexParts.join("|")})`, "g");
  }

  let lastIndex = 0;
  let match;

  if (regex) {
    while ((match = regex.exec(text)) !== null) {
      const before = text ? text.slice(lastIndex, match.index) : "";
      if (before) elements.push(<HighlightedText key={`${lastIndex}-before`} text={before} query={highlightQuery} />);

      const val = match[0];
      const value = variableKeys[val]?.label ?? val;
      const placeholder = metaMap[val] || (variableKeys[val] ? val : null);

      if (placeholder) {
        const style = variableKeys[placeholder] || { borderColor: "#FFD476", color: "#fff" };
        elements.push(
          <span
            key={lastIndex + "-" + val}
            style={{
              borderBottom: `3px solid ${style.borderColor}`,
              padding: "2px 4px 0px",
              margin: "0px 2px 2px 0px",
              display: "inline-block",
              backgroundColor: "transparent",
              fontSize: cell ? "14px" : "16px",
              color: val == 'ct__' ? '#958EA4' : '#333'
            }}
          >
            <HighlightedText text={value} query={highlightQuery} />
          </span>
        );
      } else {
        elements.push(<HighlightedText key={`${lastIndex}-value`} text={val} query={highlightQuery} />);
      }

      lastIndex = regex.lastIndex;
    }

    const after = text ? text.slice(lastIndex) : "";
    if (after) elements.push(<HighlightedText key={`${lastIndex}-after`} text={after} query={highlightQuery} />);
  } else {
    elements.push(<HighlightedText key="text" text={text} query={highlightQuery} />);
  }

  return <>{elements}</>;
};

export const NarrativeStr = (text = "", item) => {
  if (!text) return "";

  const variableKeys = getNarrativeMapping(item);
  const keys = Object.keys(variableKeys);

  if (keys.length === 0) return text;

  // escape regex
  const escapedKeys = keys.map(k =>
    k.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&")
  );

  const regex = new RegExp(`(${escapedKeys.join("|")})`, "g");

  return text.replace(regex, (match) => {
    return variableKeys[match]?.label ?? match;
  });
};

export const getNarrativeMapping = (item) => {
  const apps = item?.apps ?? [];
  const type =
    apps.includes("Outlook") || apps.includes("Teams")
      ? "subject"
      : apps.includes("Word") ||
        apps.includes("Excel") ||
        apps.includes("PowerPoint") ||
        apps.includes("PDF")
        ? "document"
        : "topic";
  const variableKeys = {
    ct__: {
      label: "Select Contact",
      borderColor: "#FFD476",
      color: "#fff",
    },
    ct__: {
      label: "Select Matter",
      borderColor: "#90CBF9",
      color: "#fff",
    },
    ct__: {
      label: "Select Client",
      borderColor: "#D9ADE1",
      color: "#fff",
    },
    __activity_title__: {
      label: type,
      borderColor: "#B7AEC6",
      color: "#fff",
    },
  };
  return variableKeys;
}

export const getVisibleSourceIconCount = (
  availableWidth,
  iconCount,
  iconSize = 24,
  gap = 4
) => {
  if (!iconCount || availableWidth <= 0) return 0;

  const fullWidth = iconCount * iconSize + Math.max(0, iconCount - 1) * gap;
  if (fullWidth <= availableWidth) return iconCount;

  const hiddenCountDigits = String(iconCount).length;
  const moreButtonWidth = 32 + Math.max(0, hiddenCountDigits - 1) * 8;
  return Math.max(
    0,
    Math.min(
      iconCount - 1,
      Math.floor((availableWidth - moreButtonWidth) / (iconSize + gap))
    )
  );
};

export const SOURCE_OVERFLOW_BACKGROUND = "rgba(74, 68, 89, 0.08)";

export const SourceIconOverflow = ({
  icons = /** @type {string[]} */ ([]),
  renderIcon,
  iconSize = 24,
  gap = 4,
  popoverLabel = "More sources",
  maxVisibleIcons = Infinity,
  overflowBadge = false,
  gridLayout = false,
  clickToExpand = false,
}) => {
  const containerRef = useRef(null);
  const moreButtonRef = useRef(null);
  const popoverRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ left: 0, top: 0 });

  const visibleCount = Number.isFinite(maxVisibleIcons)
    ? Math.min(maxVisibleIcons - (gridLayout && icons.length > maxVisibleIcons ? 1 : 0), icons.length)
    : availableWidth == null
      ? icons.length
      : getVisibleSourceIconCount(availableWidth, icons.length, iconSize, gap);
  const hiddenIcons = icons.slice(visibleCount);
  const gridItemCount = visibleCount + (hiddenIcons.length ? 1 : 0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateWidth = () => setAvailableWidth(container.clientWidth);
    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const clearOpenTimer = () => {
    if (openTimerRef.current == null) return;
    clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current == null) return;
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const updatePopoverPosition = () => {
    const button = moreButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const width = Math.min(214, Math.max(72, hiddenIcons.length * (iconSize + gap) + 16));
    const left = Math.min(
      Math.max(8, clickToExpand ? rect.left : rect.right - width),
      Math.max(8, window.innerWidth - width - 8)
    );
    const showAbove = window.innerHeight - rect.bottom < iconSize + 32;
    setPopoverPosition({
      left,
      top: showAbove ? Math.max(8, rect.top - iconSize - 24) : rect.bottom + 8,
    });
  };

  const openPopover = (delay = 1000) => {
    clearCloseTimer();
    clearOpenTimer();
    openTimerRef.current = setTimeout(() => {
      updatePopoverPosition();
      setPopoverOpen(true);
      openTimerRef.current = null;
    }, delay);
  };

  const closePopover = () => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setPopoverOpen(false);
      closeTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    if (!popoverOpen) return undefined;
    const reposition = () => updatePopoverPosition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [popoverOpen, hiddenIcons.length]);

  // MIRA-4419: Tap-open mobile overflow stays open until dismissed outside or with Escape.
  useEffect(() => {
    if (!clickToExpand || !popoverOpen) return undefined;
    const dismissOutside = (event) => {
      if (!moreButtonRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) {
        setPopoverOpen(false);
      }
    };
    const dismissOnEscape = (event) => {
      if (event.key === "Escape") setPopoverOpen(false);
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissOnEscape);
    };
  }, [clickToExpand, popoverOpen]);

  useEffect(() => () => {
    clearOpenTimer();
    clearCloseTimer();
  }, []);

  if (!icons.length) return null;

  return (
    <>
      <div
        ref={containerRef}
        style={{
          display: "flex",
          // A single row is sized to the exact overlap width. Keep up to three
          // items on that row so fractional layout rounding cannot wrap the
          // last icon onto a second line.
          flexWrap: gridLayout ? (gridItemCount > 3 ? "wrap" : "nowrap") : undefined,
          // Center full and partial source rows within the time rail.
          justifyContent: gridLayout ? "center" : undefined,
          alignItems: "center",
          gap: gridLayout ? 0 : gap,
          minWidth: 0,
          width: gridLayout
            ? Math.min(3, visibleCount + (hiddenIcons.length ? 1 : 0)) * (iconSize - 7) + 7
            : "100%",
          overflow: "hidden",
        }}
      >
        {icons.slice(0, visibleCount).map((icon, index) => gridLayout ? (
          <span
            key={`${icon}-${index}`}
            style={{
              display: "flex",
              flex: `0 0 ${iconSize}px`,
              width: iconSize,
              height: iconSize,
              padding: 1,
              boxSizing: "border-box",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: index % 3 === 0 ? 0 : -7,
            }}
          >
            {renderIcon(icon, index)}
          </span>
        ) : renderIcon(icon, index))}
        {hiddenIcons.length > 0 && (
          <button
            ref={moreButtonRef}
            type="button"
            aria-label={`Show ${hiddenIcons.length} more sources`}
            aria-expanded={popoverOpen}
            onFocus={clickToExpand ? undefined : () => openPopover(0)}
            onBlur={clickToExpand ? undefined : closePopover}
            onMouseEnter={clickToExpand ? undefined : () => openPopover()}
            onMouseLeave={clickToExpand ? undefined : closePopover}
            onClick={clickToExpand ? (event) => {
              event.stopPropagation();
              updatePopoverPosition();
              setPopoverOpen(value => !value);
            } : undefined}
            style={{
              height: gridLayout ? iconSize : overflowBadge ? 24 : 28,
              minWidth: gridLayout ? iconSize : overflowBadge ? 24 : 32,
              marginLeft: gridLayout ? (visibleCount % 3 === 0 ? 0 : -7) : overflowBadge ? -4 : undefined,
              flexShrink: 0,
              padding: overflowBadge ? 0 : "0 7px",
              boxSizing: "border-box",
              border: overflowBadge ? "2px solid #fff" : 0,
              borderRadius: overflowBadge ? "50%" : 6,
              background: overflowBadge ? "#D9D9D9" : SOURCE_OVERFLOW_BACKGROUND,
              color: overflowBadge ? "#fff" : "#333",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: gridLayout ? 11 : 14,
              fontWeight: 600,
              lineHeight: "20px",
              letterSpacing: overflowBadge ? "0.25px" : undefined,
              cursor: "pointer",
            }}
          >
            {overflowBadge ? hiddenIcons.length : `+${hiddenIcons.length}`}
          </button>
        )}
      </div>

      {popoverOpen && hiddenIcons.length > 0 && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={popoverLabel}
          onClick={clickToExpand ? event => event.stopPropagation() : undefined}
          onMouseEnter={clickToExpand ? undefined : clearCloseTimer}
          onMouseLeave={clickToExpand ? undefined : closePopover}
          style={{
            position: "fixed",
            zIndex: 10050,
            left: popoverPosition.left,
            top: popoverPosition.top,
            display: "flex",
            maxWidth: 214,
            flexWrap: "wrap",
            alignItems: "center",
            gap,
            padding: 8,
            border: "1px solid #E1E1E1",
            borderRadius: 4,
            background: "#fff",
            boxShadow: "0 1px 2px rgba(0,0,0,0.30), 0 1px 3px 1px rgba(0,0,0,0.15)",
          }}
        >
          {hiddenIcons.map((icon, index) => renderIcon(icon, visibleCount + index, true))}
        </div>,
        document.body
      )}
    </>
  );
};

export const RenderSourceIcons = ({ item, disableClockingClick = false, onClockingActionStart = null, onClockingChanged, muted = false, collapseOverflow = false, maxVisibleIcons = Infinity, overflowBadge = false, gridLayout = false, iconSize = 24 }) => {
  const [open, setOpen] = useState(false);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const popupRef = useRef(null);
  const validLinks = (item.external_links || []).filter(link => link?.url);
  const { setObject, setClockingLoading } = useWebpush();
  const { showNotification } = useNotification();
  const [clockingSubmitting, setClockingSubmitting] = useState(false);

  const hasMultipleLinks = validLinks && validLinks.length > 1;
  const showReminderCalendarHint = isReminderCalendarItem(item);
  const reminderCalendarHint = 'The duration is 0h as this may be a reminder with its calendar status set to "Free".';

  // item.source_icons can include the Clocking icon because one of item.slots is a
  // Clocking slot, even when item.apps/non_svc_source itself doesn't say "Clocking" -
  // treat that case as a clocking item too so the icon stays interactive/running.
  const hasClockingSource = (target) => isClockingItem(target) || (Array.isArray(target?.slots) && target.slots.some(isClockingItem));

  const getClockingLookupQuery = () => {
    const today = moment().format("YYYY-MM-DD");
    const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
    const params = [];

    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const delegatorId = sessionStorage.getItem("delegators");
      if (user?.id && delegatorId && +user.id !== +delegatorId) {
        params.push(`delegators=${encodeURIComponent(delegatorId)}`);
      }
    } catch {
      // Keep clocking usable even if cached user data is not parseable.
    }

    params.push(`start_date=${toLocalStartOfDay(yesterday)}`);
    params.push(`end_date=${toLocalEndOfDay(today)}`);

    return `?${params.join("&")}`;
  };

  const handleClockingClick = async (e) => {
    e.stopPropagation();
    if (clockingSubmitting || !hasClockingSource(item) || isFinalizedClocking(item)) return;

    onClockingActionStart?.();
    setClockingSubmitting(true);
    setClockingLoading(true);
    const stoppedTimeentryIds = [];
    const stoppedSlotPatches = [];
    try {
      const targetTimeentryId = item.timeentry ?? item.id;
      const sameTimeentry = (a, b) => String(a ?? "") === String(b ?? "");
      let recentRunningSlots = null;
      const getRecentRunningSlots = async () => {
        if (recentRunningSlots) return recentRunningSlots;
        const recentSlots = await getTimeSlots(getClockingLookupQuery());
        recentRunningSlots = (recentSlots || []).filter(slot => isClockingItem(slot) && isActuallyRunningClocking(slot));
        return recentRunningSlots;
      };

      let runningSlot = (item.slots || []).find(isActuallyRunningClocking)
        || (isActuallyRunningClocking(item) ? item : null);

      if (!runningSlot && isRunningClocking(item)) {
        const runningSlots = await getRecentRunningSlots();
        runningSlot = runningSlots.find(slot => sameTimeentry(slot.timeentry, targetTimeentryId)) || null;
      }

      console.log("[ClockingClick] start", { itemId: item.id, tkprUsername: item.tkpr_username, runningSlotId: runningSlot?.id ?? null });

      if (runningSlot) {
        const stopPayload = { id: runningSlot.id, stop: new Date().toISOString(), tkpr_username: item.tkpr_username ?? null };
        console.log("[ClockingClick] stop payload", stopPayload);
        await updateTimeSlot([stopPayload]);
        const stoppedTimeentryId = runningSlot.timeentry ?? item.timeentry ?? item.id;
        console.log("[ClockingClick] emitting clocking.stop", { type: 'timeentry', id: stoppedTimeentryId });
        setObject({
          action: 'clocking.stop',
          type: 'timeentry',
          id: stoppedTimeentryId,
          skipGridRefresh: true,
          skipTimelineRefresh: true,
          stoppedSlots: [{
            ...runningSlot,
            id: runningSlot.id,
            timeentry: stoppedTimeentryId,
            stop: stopPayload.stop,
          }],
        });
        useClockingStore.getState().applyClockingChange({
          stoppedSlots: [{
            ...runningSlot,
            id: runningSlot.id,
            timeentry: stoppedTimeentryId,
            stop: stopPayload.stop,
          }],
        });
        // Refresh the affected entry locally right away so its status/time updates without
        // waiting for the webpush round-trip, then hide the overlay immediately - don't wait
        // for the (slower) webpush-driven refresh to clear it. Also pass an optimistic patch
        // so the running icon flips to idle instantly, without waiting for that refresh either.
        await onClockingChanged?.([stoppedTimeentryId], {
          stoppedSlots: [{ id: runningSlot.id, timeentry: stoppedTimeentryId, stop: stopPayload.stop }],
        });
        setClockingLoading(false);
      } else {
        if (isRunningClocking(item)) {
          showNotification({ type: 'error', message: 'Clocking is already running.' });
          setClockingLoading(false);
          return;
        }

        const payload = {
          timeentry: item.timeentry ?? item.id,
          start: new Date().toISOString(),
          stop: null,
          non_svc_source: "Clocking",
          apps: ["Clocking"],
          matter_number: item.matter_number ?? null,
          matter_name: item.matter_name ?? null,
          matter_id: item.matter_id ?? null,
          matter_type: item.matter_type ?? null,
          client_id: item.client_id ?? null,
          client_number: item.client_number ?? null,
          client_name: item.client_name ?? null,
          is_billable: !!item.is_billable,
          tkpr_username: item.tkpr_username ?? null,
        };

        // Stop any other running clocking first, then create the new slot - the previous
        // clocking must be closed off before the new one starts running.
        const stopOtherRunningClocking = async () => {
          try {
            const runningSlots = await getRecentRunningSlots();
            console.log("[ClockingClick] recent running slots", runningSlots.map(s => ({ id: s.id, timeentry: s.timeentry, status: s.status, stop: s.stop })), "target", targetTimeentryId);
            const otherRunningSlots = runningSlots.filter(slot => !sameTimeentry(slot.timeentry, targetTimeentryId));

            await Promise.all(otherRunningSlots.map(async (otherRunningSlot) => {
              const otherStopPayload = { id: otherRunningSlot.id, stop: new Date().toISOString(), tkpr_username: otherRunningSlot.tkpr_username ?? item.tkpr_username ?? null };
              console.log("[ClockingClick] stopping other running clocking", otherStopPayload);
              await updateTimeSlot([otherStopPayload]);
              if (otherRunningSlot.timeentry != null) {
                stoppedTimeentryIds.push(otherRunningSlot.timeentry);
                stoppedSlotPatches.push({ id: otherRunningSlot.id, timeentry: otherRunningSlot.timeentry, stop: otherStopPayload.stop });
              }
            }));
          } catch (error) {
            console.error("[ClockingClick] failed to stop other running clocking", error);
            // Surface this instead of failing silently - otherwise the new clocking starts
            // while the previous one keeps running, with no indication of why.
            showNotification({ type: 'error', message: 'Started clocking, but failed to stop the previously running clocking. Please stop it manually.' });
          }
        };

        await stopOtherRunningClocking();
        // Apply the "stopped" style as soon as the stop PATCH(es) above complete, instead
        // of bundling it with the started slot below - that bundle only happens after the
        // create POST (and its webpush round-trip) finishes, which delayed this far longer
        // than necessary.
        if (stoppedSlotPatches.length) {
          await onClockingChanged?.(stoppedTimeentryIds, { stoppedSlots: stoppedSlotPatches });
        }
        console.log("[ClockingClick] start payload", payload);
        const created = await createTimeSlots([payload]);
        const createdSlotId = getCreatedClockingSlotId(created);
        if (createdSlotId == null) {
          throw new Error('The time slot response did not include an id.');
        }
        console.log("[ClockingClick] created", { id: createdSlotId });
        const optimisticStartedSlot = {
          ...payload,
          id: createdSlotId,
          isActuallyTiming: true,
          isTiming: true,
          timeentry_data: {
            ...item,
            id: targetTimeentryId,
          },
        };
        console.log("[ClockingClick] emitting clocking.start", { type: 'timeentry', id: item.id, stoppedTimeentryIds });
        setObject({
          action: 'clocking.start',
          type: 'timeentry',
          id: targetTimeentryId,
          stoppedTimeentryIds,
          stoppedSlots: stoppedSlotPatches,
          startedSlot: optimisticStartedSlot,
          skipGridRefresh: true,
          skipTimelineRefresh: true,
        });
        useClockingStore.getState().applyClockingChange({
          startedSlot: optimisticStartedSlot,
          stoppedSlots: stoppedSlotPatches,
        });
        // Refresh the started entry locally right away so its status/time updates without
        // waiting for the webpush round-trip, then hide the overlay immediately - don't wait
        // for the (slower) webpush-driven refresh to clear it. Also pass an optimistic patch
        // so the new running icon flips instantly, without waiting for that refresh either.
        await onClockingChanged?.([targetTimeentryId], {
          // Build the optimistic slot from the create payload (always complete) merged with
          // whatever the response gave us, so this never depends on the response shape -
          // and fall back to a synthetic id if the response didn't include a real one.
          startedSlot: optimisticStartedSlot,
        });
        setClockingLoading(false);
      }
    } catch (error) {
      console.error("[ClockingClick] failed", error);
      if (stoppedTimeentryIds.length) {
        const [id, ...relatedTimeentryIds] = stoppedTimeentryIds;
        setObject({ action: 'clocking.stop', type: 'timeentry', id, relatedTimeentryIds });
        onClockingChanged?.(stoppedTimeentryIds, { stoppedSlots: stoppedSlotPatches });
      }
      showNotification({ type: 'error', message: error?.message || 'Failed to update clocking. Please try again.' });
      // No refresh will be triggered to clear the loader if setObject was never called above.
      setClockingLoading(false);
    } finally {
      setClockingSubmitting(false);
    }
  };

  const handleClick = (e) => {
    if (!validLinks || validLinks.length === 0) return;

    const targetLink = validLinks[0];

    window.open(
      targetLink.url,
      "_blank",
      "noopener,noreferrer"
    );
    // if (hasMultipleLinks) {
    //   setPopupAnchor(e.currentTarget);
    //   setOpen(!open);
    // } else {
    //   window.open(validLinks[0].url, "_blank", "noopener,noreferrer");
    // }
  };

  // Close the popup when the user clicks outside it.
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        popupAnchor &&
        !popupAnchor.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, popupAnchor]);

  // Only today's clocking can start/stop because the click creates or updates a slot at the current time.
  const isClockingItemToday = (target) => {
    const dateStr = target.date || target.start || target.dt;
    if (!dateStr) return true;
    return moment(dateStr).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
  };

  const renderSourceIcon = (icon, i) => {
    const isClockingIcon = icon === SOURCE_CLOCKING_ICON || icon === SOURCE_CLOCKING_RUNNING_ICON;
    const isStaticClockingIcon = icon === SOURCE_CLOCKING_STATIC_ICON;
    const isInteractiveClockingIcon = isClockingIcon && hasClockingSource(item) && !isFinalizedClocking(item) && !disableClockingClick && isClockingItemToday(item);
    const isDisabledClockingIcon = (isClockingIcon || isStaticClockingIcon) && !isInteractiveClockingIcon;
    const displayIcon = isDisabledClockingIcon ? SOURCE_CLOCKING_STATIC_ICON : icon;
    const sourceMuted = muted || isNoiseEntry(item) || isNoiseItem(item);

    const image = (
      <img
        key={i}
        src={displayIcon}
        onClick={isInteractiveClockingIcon ? handleClockingClick : (isDisabledClockingIcon ? (e) => e.stopPropagation() : undefined)}
        style={{
          width: gridLayout ? iconSize - 2 : iconSize,
          height: gridLayout ? iconSize - 2 : iconSize,
          objectFit: "contain",
          flexShrink: 0,
          cursor: isInteractiveClockingIcon ? "pointer" : "inherit",
          opacity: isInteractiveClockingIcon && clockingSubmitting ? 0.5 : 1,
          pointerEvents: isInteractiveClockingIcon && clockingSubmitting ? "none" : "auto",
          filter: sourceMuted ? "saturate(0)" : undefined,
        }}
      />
    );

    return image;
  };

  return (
    <>
      <div
        style={{
          minWidth: iconSize,
          display: "flex",
          gap: 4,
          flex: collapseOverflow ? "0 1 auto" : undefined,
          flexShrink: collapseOverflow ? 1 : 0,
          overflow: collapseOverflow ? "hidden" : undefined,
          cursor: validLinks?.length ? "pointer" : "default",
        }}
        onClick={handleClick}
      >
        {collapseOverflow ? (
          <SourceIconOverflow
            icons={item.source_icons || []}
            renderIcon={renderSourceIcon}
            maxVisibleIcons={maxVisibleIcons}
            overflowBadge={overflowBadge}
            gridLayout={gridLayout}
            iconSize={iconSize}
            gap={overflowBadge ? 0 : 4}
          />
        ) : (
          item.source_icons?.map((icon, i) => renderSourceIcon(icon, i))
        )}
      </div>

      {hasMultipleLinks && open && popupAnchor && (
        <Popup anchor={popupAnchor} show={open} className="sysnotes-popup">
          <div
            ref={popupRef}
            style={{
              background: "#fff",
              borderRadius: 4,
              padding: "8px 0px",
              maxHeight: "200px",
              overflowY: "scroll",
            }}
          >
            {validLinks.map((link, idx) => (
              <div
                key={idx}
                className="sysnotes-menu-item"
                onClick={() => {
                  window.open(link.url, "_blank", "noopener,noreferrer");
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 12px",
                  cursor: "pointer",
                  gap: 8,
                }}
              >
                <img src={item.source_icons[idx] || link.icon} width={17} height={17} />
                <span style={{ flex: 1 }}>{link.title || "Open Link"}</span>
                <img src={HYPER_LINK} />
              </div>
            ))}
          </div>
        </Popup>
      )}
    </>
  );
};

const getWorkedOnText = (worked_on) => {
  if (!worked_on) return "";

  // 去掉所有 HTML 标签
  const sanitized = worked_on.replace(/<[^>]+>/g, "");

  // 去掉首尾多余空格
  return sanitized.trim();
};

// ─────────────────────── Outlook email helpers ───────────────────────

// Keep card labels human-readable, while tooltip callers prefer the full
// address and fall back to the name for legacy records without email data.
const getContactsByRole = (contacts, role, preferEmail = false) =>
  (contacts || [])
    .filter(c => c?.role?.toLowerCase() === role)
    .map(c => preferEmail ? (c?.email || c?.name || '') : (c?.name || c?.email || ''))
    .filter(Boolean);

const normalizeParticipantValue = (value) =>
  String(value || '').trim().toLowerCase();

const addUserAliases = (aliases, user) => {
  if (!user) return;

  if (typeof user === 'string') {
    const value = normalizeParticipantValue(user);
    if (value) aliases.add(value);
    return;
  }

  [
    user.email,
    user.username,
    user.name,
    user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : null,
  ].forEach(value => {
    const normalized = normalizeParticipantValue(value);
    if (normalized) aliases.add(normalized);
  });
};

const getStoredLoginUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (e) {
    return null;
  }
};

const getSelfParticipantAliases = (item, selectedUser) => {
  const aliases = new Set();

  addUserAliases(aliases, selectedUser);
  addUserAliases(aliases, item?.tkpr_username);
  addUserAliases(aliases, item?.email);
  addUserAliases(aliases, getStoredLoginUser());
  addUserAliases(aliases, localStorage.getItem("email"));

  return aliases;
};

const isSelfParticipant = (participant, aliases) => {
  const normalized = normalizeParticipantValue(participant);
  if (!normalized) return false;
  if (aliases.has(normalized)) return true;

  return Array.from(aliases)
    .filter(alias => alias.includes("@"))
    .some(email => normalized.includes(email));
};

const getOutlookParticipants = (metadata) => {
  if (!metadata?.type) return [];
  const contacts = metadata.contacts || [];
  if (metadata.type?.toLowerCase() === 'read') {
    return getContactsByRole(contacts, 'from');
  }
  return [
    ...getContactsByRole(contacts, 'to'),
    ...getContactsByRole(contacts, 'cc'),
    ...getContactsByRole(contacts, 'bcc'),
  ];
};

const capitalizeWords = (sentence) => {
  return sentence
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const EmailTypeBadge = ({ emailType }) => {
  return (
    <span style={{
      padding: '0 4px',
      borderRadius: 3,
      fontSize: 10,
      fontWeight: 600,
      background: '#DADEDF',
      color: '#333',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      borderRadius: 6,
      border: '1px solid rgba(51, 51, 51, 0.12)',
    }}>
      {capitalizeWords(emailType)}
    </span>
  );
};

// Shared tooltip style for Outlook email components
const OUTLOOK_TOOLTIP_STYLE = {
  maxWidth: 280,
  border: 'none',
  backgroundColor: '#000',
  color: '#fff',
  padding: '6px 10px',
  fontSize: 13,
  fontWeight: 400,
};

export const buildSlotTooltipLines = (meta, subject) => {
  if (!meta) return null;
  const contacts = meta.contacts || [];
  const lines = [];
  if (meta.type?.toLowerCase() === 'read') {
    const from = getContactsByRole(contacts, 'from', true);
    if (from.length) lines.push({ label: 'From', value: from.join(', ') });
  } else {
    const to  = getContactsByRole(contacts, 'to', true);
    const cc  = getContactsByRole(contacts, 'cc', true);
    const bcc = getContactsByRole(contacts, 'bcc', true);
    if (to.length)  lines.push({ label: 'To',  value: to.join(', ') });
    if (cc.length)  lines.push({ label: 'CC',  value: cc.join(', ') });
    if (bcc.length) lines.push({ label: 'BCC', value: bcc.join(', ') });
  }
  if (subject) lines.push({ label: 'Subject', value: subject });
  return lines.length ? lines : null;
};

const TooltipLinesList = ({ lines }) => (
  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
    {lines.map(({ label, value }, idx) => (
      <div key={label || idx}>{label ? `${label}: ${value}` : value}</div>
    ))}
  </div>
);

const MobileEmailPopup = ({ anchor, popupRef, lines }) => {
  if (!anchor) return null;
  return (
    <Popup
      anchor={anchor}
      show={true}
      popupAlign={{ horizontal: 'center', vertical: 'top' }}
      anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
    >
      <div
        ref={popupRef}
        style={{ background: '#333', color: '#fff', borderRadius: 6, padding: '8px 12px', fontSize: 13, lineHeight: 1.6, maxWidth: 260 }}
      >
        <TooltipLinesList lines={lines} />
      </div>
    </Popup>
  );
};

const HoverActionPopupInteractionContext = createContext(null);
const HoverActionPopupCloseContext = createContext(null);

export const InlineDurationEditor = ({
  item,
  durationSeconds,
  durationParts,
  canEdit = true,
  disabled = false,
  isNoise = false,
  onSave,
  textStyle = {},
  editorGap = 6,
  editLayout = "inline",
  inputWidth = 30,
  editorStyle = {},
  onEditingChange,
  alwaysShowEditIcon = false,
  editIconPosition = "left",
  durationStart,
  initialEditing = false,
  editorClassName = "",
  prefillSeconds = undefined,
  initialFocusField = undefined,
  targetType = "slot",
  requireConfirmation = false,
  onSavingChange = /** @type {(isSaving: boolean) => void} */ (() => {}),
}) => {
  const wrapperRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const secondRef = useRef(null);
  const saveButtonRef = useRef(null);
  const closeHoverActionPopup = useContext(HoverActionPopupCloseContext);
  const [editing, setEditing] = useState(initialEditing);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const mountedRef = useRef(true);
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const userInteractedRef = useRef(!initialEditing);
  const saveInProgressRef = useRef(false);
  const saveEditRef = useRef(null);
  const popupEnteredRef = useContext(HoverActionPopupInteractionContext);
  const storeMinMinutes = useTimeEntriesStore(state => state.minMinutes);

  const normalizedSeconds = Math.max(0, Math.round(Number(durationSeconds) || 0));
  const toParts = (seconds) => ({
    h: Math.floor(seconds / 3600),
    m: Math.floor((seconds % 3600) / 60),
    s: seconds % 60,
  });
  const resolvedPrefillSeconds = prefillSeconds == null
    ? (initialEditing ? getMinIncrementSeconds(storeMinMinutes) : 0)
    : Math.max(0, Math.round(Number(prefillSeconds) || 0));
  const initialEditorSeconds = normalizedSeconds || resolvedPrefillSeconds;
  const durationSlot = item?.slots?.find?.(slot => slot?.start || slot?.start_display);
  const resolvedDurationStart = [
    item?.start,
    durationSlot?.start,
    durationStart,
    item?.start_display,
    item?.start_time,
    item?.startTime,
    item?.started_at,
    durationSlot?.start_display,
  ];
  const maxDurationParts = toParts(getMaxDurationSecondsForStart(resolvedDurationStart));
  const getPartMax = (field, sourceParts = parts) => {
    if (field === "h") return maxDurationParts.h;
    if (field === "m") return sourceParts.h >= maxDurationParts.h ? maxDurationParts.m : 59;
    if (field === "s") {
      return sourceParts.h >= maxDurationParts.h && sourceParts.m >= maxDurationParts.m
        ? maxDurationParts.s
        : 59;
    }
    return 59;
  };
  const clampParts = (nextParts) => {
    const nextSeconds = (Number(nextParts.h) || 0) * 3600 + (Number(nextParts.m) || 0) * 60 + (Number(nextParts.s) || 0);
    return toParts(clampDurationSecondsToStartDay(nextSeconds, resolvedDurationStart));
  };
  const [displaySeconds, setDisplaySeconds] = useState(normalizedSeconds);
  const [parts, setParts] = useState(toParts(initialEditorSeconds));
  const [, forceInputSync] = useState(0);
  const [inputVersions, setInputVersions] = useState({ h: 0, m: 0, s: 0 });
  const pendingFocusFieldRef = useRef(null);
  useSyncClampedNumericTextBoxValue(hourRef, parts.h, 0);
  useSyncClampedNumericTextBoxValue(minuteRef, parts.m, 0);
  useSyncClampedNumericTextBoxValue(secondRef, parts.s, 0);

  useEffect(() => {
    setDisplaySeconds(normalizedSeconds);
    if (!editing) setParts(toParts(normalizedSeconds));
  }, [normalizedSeconds]);

  useEffect(() => {
    if (!editing || normalizedSeconds || !resolvedPrefillSeconds) return;
    setParts(prev => {
      const currentSeconds = (Number(prev.h) || 0) * 3600 + (Number(prev.m) || 0) * 60 + (Number(prev.s) || 0);
      if (currentSeconds) return prev;
      return toParts(resolvedPrefillSeconds);
    });
  }, [editing, normalizedSeconds, resolvedPrefillSeconds]);

  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing]);

  const getInputElement = (ref) => {
    const element = ref.current?.element;
    if (!element) return null;
    return element.tagName === "INPUT" ? element : element.querySelector?.("input");
  };

  const focusInput = (ref) => {
    ref.current?.focus?.();
    const input = getInputElement(ref);
    input?.focus?.();
    input?.select?.();
  };

  useEffect(() => {
    if (!initialEditing) return;
    const focusRefs = { h: hourRef, m: minuteRef, s: secondRef };
    const focusField = initialFocusField === "h" || initialFocusField === "s"
      ? initialFocusField
      : "m";
    setTimeout(() => focusInput(focusRefs[focusField]), 0);
  }, []);

  useLayoutEffect(() => {
    if (!editing || !pendingFocusFieldRef.current) return;

    const focusRef = {
      h: hourRef,
      m: minuteRef,
      s: secondRef,
    }[pendingFocusFieldRef.current];
    pendingFocusFieldRef.current = null;

    const input = getInputElement(focusRef);
    input?.focus?.();
    const cursorPosition = input?.value?.length ?? 0;
    input?.setSelectionRange?.(cursorPosition, cursorPosition);
  });

  const startEdit = (e) => {
    e?.stopPropagation?.();
    if (!canEdit || disabled || isNoise || saveInProgressRef.current) return;
    // Re-opening the collapsed value is an explicit user action. This must
    // commit the minimum-increment prefill when focus leaves the editor.
    userInteractedRef.current = true;
    setParts(toParts(clampDurationSecondsToStartDay(displaySeconds, resolvedDurationStart)));
    setEditing(true);
    setHovered(false);
    setFocused(false);
    setTimeout(() => focusInput(hourRef), 0);
  };

  const cancelEdit = () => {
    if (!mountedRef.current) return;
    setParts(toParts(displaySeconds));
    setEditing(false);
    setHovered(false);
    setFocused(false);
  };

  const saveEdit = async () => {
    if (saveInProgressRef.current) return;

    // Popup editors open and focus automatically. Merely hovering must not
    // apply the prefilled min increment; require an actual click/key/change.
    if (initialEditing && !userInteractedRef.current && !popupEnteredRef?.current) {
      cancelEdit();
      return;
    }

    saveInProgressRef.current = true;

    const requestedSeconds = (Number(parts.h) || 0) * 3600 + (Number(parts.m) || 0) * 60 + (Number(parts.s) || 0);
    const nextSeconds = clampDurationSecondsToStartDay(requestedSeconds, resolvedDurationStart);
    if (!nextSeconds) {
      if (!requireConfirmation) cancelEdit();
      saveInProgressRef.current = false;
      return;
    }

    // An unavailable-time popup may already display the entry's persisted
    // duration while its underlying slot still has no stop. After an explicit
    // interaction, send the same duration so the backend can persist stop.
    if (nextSeconds === displaySeconds && !initialEditing) {
      if (mountedRef.current) {
        setEditing(false);
        setHovered(false);
        setFocused(false);
      }
      saveInProgressRef.current = false;
      return;
    }

    const parsedStart = parseDurationStart(resolvedDurationStart);
    const saveStart = item?.start || durationSlot?.start || (
      parsedStart && !isNaN(parsedStart) ? parsedStart.toISOString() : null
    );
    if (!item?.id || !saveStart) {
      if (mountedRef.current) {
        setEditing(false);
        setHovered(false);
        setFocused(false);
      }
      if (requireConfirmation) closeHoverActionPopup?.();
      saveInProgressRef.current = false;
      return;
    }

    const stop = moment(saveStart).add(nextSeconds, "seconds").toISOString();
    const payload = { id: item.id, start: saveStart, stop };
    onSavingChange?.(true);
    try {
      // Once the user saves, dismiss the hover tooltip immediately and leave
      // the card-level duration skeleton visible while the request completes.
      closeHoverActionPopup?.();
      if (targetType === "entry") {
        await updateTimeEntries(item.id, {
          start: saveStart,
          stop,
          duration: nextSeconds,
        });
      } else {
        await updateTimeSlot([payload]);
      }
      if (mountedRef.current) {
        setDisplaySeconds(nextSeconds);
        setParts(toParts(nextSeconds));
        setEditing(false);
        setHovered(false);
        setFocused(false);
      }
      await onSave?.(payload);
    } finally {
      onSavingChange?.(false);
      saveInProgressRef.current = false;
    }
  };

  saveEditRef.current = saveEdit;

  useEffect(() => () => {
    // A popup can unmount without moving focus first. If the user actually
    // interacted with this editor, commit here instead of relying on blur.
    const shouldCommit = !requireConfirmation && editingRef.current && (
      userInteractedRef.current || popupEnteredRef?.current
    );
    mountedRef.current = false;
    if (shouldCommit) saveEditRef.current?.();
  }, []);

  const handleBlurCapture = () => {
    if (requireConfirmation) return;

    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        saveEdit();
      }
    }, 0);
  };

  const preventParentDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const markUserInteraction = () => {
    userInteractedRef.current = true;
  };

  const updatePart = (field, value, max) => {
    userInteractedRef.current = true;
    const numeric = value == null ? 0 : Math.max(0, Math.min(max, Math.floor(Number(value) || 0)));
    setParts(prev => clampParts({ ...prev, [field]: numeric }));
    forceInputSync(version => version + 1);
  };

  const forcePartToMax = (field, max) => {
    updatePart(field, max, max);
    pendingFocusFieldRef.current = field;
    setInputVersions(prev => ({ ...prev, [field]: prev[field] + 1 }));
  };

  useEffect(() => {
    if (!editing) return undefined;

    const bindImmediateMax = (field, ref) => {
      const input = getInputElement(ref);
      if (!input) return undefined;

      const getNextValue = (event) => {
        if (!event.data) return null;

        const currentValue = String(input.value || "");
        const selectionStart = input.selectionStart ?? currentValue.length;
        const selectionEnd = input.selectionEnd ?? selectionStart;

        return `${currentValue.slice(0, selectionStart)}${event.data}${currentValue.slice(selectionEnd)}`;
      };

      const handleBeforeInput = (event) => {
        const nextValue = getNextValue(event);
        if (nextValue == null) return;

        const max = getPartMax(field);
        const nextNumber = Number(nextValue.replace(/,/g, ""));
        if (!Number.isFinite(nextNumber) || nextNumber <= max) return;

        event.preventDefault();
        forcePartToMax(field, max);
      };

      const handleInput = () => {
        const max = getPartMax(field);
        const rawValue = Number(String(input.value).replace(/,/g, ""));
        if (!Number.isFinite(rawValue) || rawValue <= max) return;

        forcePartToMax(field, max);
      };

      input.addEventListener("beforeinput", handleBeforeInput, true);
      input.addEventListener("input", handleInput, true);
      return () => {
        input.removeEventListener("beforeinput", handleBeforeInput, true);
        input.removeEventListener("input", handleInput, true);
      };
    };

    const cleanups = [
      bindImmediateMax("h", hourRef),
      bindImmediateMax("m", minuteRef),
      bindImmediateMax("s", secondRef),
    ];

    return () => cleanups.forEach(cleanup => cleanup?.());
  }, [editing, parts, resolvedDurationStart]);

  useEffect(() => {
    if (!editing) return undefined;

    const bindEnter = (fromRef, toRef) => {
      const input = getInputElement(fromRef);
      if (!input) return undefined;

      const handler = (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        e.stopPropagation();

        if (toRef) {
          focusInput(toRef);
          return;
        }

        if (requireConfirmation) {
          saveButtonRef.current?.focus?.();
          return;
        }

        saveEdit();
      };

      input.addEventListener("keydown", handler);
      return () => input.removeEventListener("keydown", handler);
    };

    const cleanups = [
      bindEnter(hourRef, minuteRef),
      bindEnter(minuteRef, secondRef),
      bindEnter(secondRef, null),
    ];

    return () => cleanups.forEach(cleanup => cleanup?.());
  }, [editing, parts]);

  const displayParts = formatDurationParts(displaySeconds / 3600);

  if (editing) {
    return (
      <span
        className={`inlineDurationEditor ${requireConfirmation ? "inlineDurationEditor--confirmation" : ""} ${editorClassName}`.trim()}
        ref={wrapperRef}
        data-ignore-close-timeslot-force
        onBlurCapture={handleBlurCapture}
        onPointerDownCapture={markUserInteraction}
        onMouseDownCapture={markUserInteraction}
        onTouchStartCapture={markUserInteraction}
        onKeyDownCapture={markUserInteraction}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onDragStart={preventParentDrag}
        draggable={false}
        style={{
          display: requireConfirmation || editLayout === "block" ? "flex" : "inline-flex",
          flexDirection: requireConfirmation ? "column" : undefined,
          alignItems: requireConfirmation ? "stretch" : "center",
          justifyContent: editLayout === "block" ? "flex-end" : undefined,
          gap: requireConfirmation ? 8 : 0,
          flex: editLayout === "block" ? "1 0 100%" : undefined,
          width: requireConfirmation || editLayout === "block" ? "100%" : undefined,
          marginTop: editLayout === "block" ? 4 : undefined,
          ...editorStyle,
        }}
      >
        <span
          className="inlineDurationEditor__inputs"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: requireConfirmation ? "flex-end" : undefined,
            gap: editorGap,
            width: requireConfirmation ? "100%" : undefined,
            paddingBottom: requireConfirmation ? 4 : undefined,
          }}
        >
          <NumericTextBox
            key={`h-${inputVersions.h}`}
            className="inlineDurationInput"
            ref={hourRef}
            value={parts.h}
            min={0}
            max={getPartMax("h")}
            style={{ width: inputWidth + 18 }}
            format="n0"
            onChange={(e) => updatePart("h", e.value, getPartMax("h"))}
          />
          <span>h</span>
          <NumericTextBox
            key={`m-${inputVersions.m}`}
            className="inlineDurationInput"
            ref={minuteRef}
            value={parts.m}
            min={0}
            max={getPartMax("m")}
            style={{ width: inputWidth + 18 }}
            format="n0"
            onChange={(e) => updatePart("m", e.value, getPartMax("m"))}
          />
          <span>m</span>
          <NumericTextBox
            key={`s-${inputVersions.s}`}
            className="inlineDurationInput"
            ref={secondRef}
            value={parts.s}
            min={0}
            max={getPartMax("s")}
            style={{ width: inputWidth + 18 }}
            format="n0"
            onChange={(e) => updatePart("s", e.value, getPartMax("s"))}
          />
          <span>s</span>
        </span>
        {requireConfirmation && (
          <span className="inlineDurationEditor__actions">
            <button
              ref={saveButtonRef}
              type="button"
              className="inlineDurationEditor__button inlineDurationEditor__button--close"
              onClick={(event) => {
                event.stopPropagation();
                cancelEdit();
                closeHoverActionPopup?.();
              }}
            >
              Close
            </button>
            <button
              type="button"
              className="inlineDurationEditor__button inlineDurationEditor__button--save"
              onClick={(event) => {
                event.stopPropagation();
                userInteractedRef.current = true;
                saveEdit();
              }}
            >
              Save
            </button>
          </span>
        )}
      </span>
    );
  }

  const showEditIcon = canEdit && !disabled && !isNoise && (alwaysShowEditIcon || hovered || focused);
  const editIcon = showEditIcon ? (
    <img
      src={EDIT_ICON}
      alt=""
      style={{
        width: 12,
        height: 12,
        alignSelf: "center",
        flexShrink: 0,
      }}
    />
  ) : null;

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={startEdit}
      onDragStart={preventParentDrag}
      draggable={false}
      tabIndex={canEdit && !disabled && !isNoise ? 0 : undefined}
      style={{
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "baseline",
        gap: 3,
        cursor: canEdit && !disabled && !isNoise ? "text" : "default",
        backgroundColor: hovered && canEdit && !disabled && !isNoise ? "#fff" : "transparent",
        borderRadius: 4,
        padding: 4,
        ...textStyle,
      }}
    >
      {editIconPosition === "left" && editIcon}
      <span style={{ textDecoration: isNoise ? "line-through" : undefined }}>
        {displayParts.map((part, idx) => (
          <React.Fragment key={`${part.unit}-${idx}`}>
            <span>{part.value}<span>{part.unit}</span></span>
            {idx !== displayParts.length - 1 && <>&nbsp;</>}
          </React.Fragment>
        ))}
      </span>
      {editIconPosition === "right" && editIcon}
    </span>
  );
};

export const RenderDurationText = ({ slot, applyMinIncrement, entryIsRunningClocking, editable = false, onEditSlotSuccess, onSavingChange = /** @type {(isSaving: boolean) => void} */ (() => {}), durationSaving = false, isNoise = false, editorGap = 6, editLayout = "inline", inputWidth = 30, editorStyle = {}, onEditingChange, alwaysShowEditIcon = false, textStyle = {}, editIconPosition = "left" }) => {
      void applyMinIncrement;
      const slotIsActuallyTiming = isActuallyRunningClocking(slot)
      return (
        <>
          {entryIsRunningClocking || slotIsActuallyTiming ? (
              <ClockingTimer start={slot.start} stop={slot.stop} />
          ) : isReminderCalendarItem(slot) && (Math.abs(slot.duration) < 0.001 || !slot.stop_display) ? (
              <Tooltip
                  parentTitle={true}
                  tooltipStyle={{
                      maxWidth: "200px",
                      border: "none",
                      backgroundColor: "#000",
                      color: "#fff",
                      padding: "4px 8px",
                      fontSize: "14px",
                      fontWeight: 400,
                  }}

                  anchorElement="target"
                  position="right"
              >
                  <span title='The duration is 0h as this may be a reminder with its calendar status set to "Free".' style={{ display: 'inline-flex', alignItems: 'baseline' }}>
                      <span>0<span>h</span></span>
                  </span>
              </Tooltip>
          ) : hasUnavailableSlotDuration(slot) ? (
              // MIRA-4414: Missing timestamps must not allow editing ignored activities.
              <>
                  <HoverActionPopup
                      icon={CARD_QUESTION_RED}
                      content={getUnavailableTimeMessage(slot)}
                      hideIcon={durationSaving}
                      actionContent={editable && slot.canEdit && !isNoise && !isNoiseItem(slot) ? (
                          <div>
                              <div style={{ marginBottom: 8 }}>Add time manually:</div>
                              <InlineDurationEditor
                                  item={slot}
                                  durationSeconds={(Number(slot.duration) || 0) * 3600}
                                  durationParts={slot.durationParts}
                                  canEdit
                                  onSave={onEditSlotSuccess}
                                  onSavingChange={onSavingChange}
                                  initialEditing
                                  requireConfirmation
                                  inputWidth={42}
                                  editorGap={4}
                                  editorClassName="inlineDurationEditor--inversePopup"
                                  editorStyle={{ justifyContent: "flex-start", color: "#fff", fontSize: 12 }}
                              />
                          </div>
                      ) : undefined}
                  />
              </>
          ) : (
              // ✅ 正常：显示 duration
              editable ? (
                  <InlineDurationEditor
                      item={slot}
                      durationSeconds={(Number(slot.duration) || 0) * 3600}
                      durationParts={slot.durationParts}
                      canEdit={slot.canEdit}
                      disabled={entryIsRunningClocking || slotIsActuallyTiming}
                      isNoise={isNoise || isNoiseItem(slot)}
                      onSave={onEditSlotSuccess}
                      onSavingChange={onSavingChange}
                      editorGap={editorGap}
                      editLayout={editLayout}
                      inputWidth={inputWidth}
                      editorStyle={editorStyle}
                      onEditingChange={onEditingChange}
                      alwaysShowEditIcon={alwaysShowEditIcon}
                      textStyle={textStyle}
                      editIconPosition={editIconPosition}
                  />
              ) : (
                  slot.durationParts.map((part, idx) => (
                      <React.Fragment key={`current-${idx}`}>
                          <span style={{ textDecoration: isNoise || isNoiseItem(slot) ? "line-through" : undefined }}>
                              {part.value}
                              <span>
                                  {part.unit}
                              </span>
                          </span>
                          &nbsp;
                      </React.Fragment>
                  ))
              )
          )}
          {!(entryIsRunningClocking || slotIsActuallyTiming) && (
              <NetDocumentsNoiseDurationHint item={slot} />
          )}
        </>
      )
}

// ── Expanded slot row inside a multi-slot Outlook entry ──
export const OutlookEmailSlotRow = ({
  slot,
  isMobile,
  isLast = false,
  applyMinIncrement,
  entryIsRunningClocking,
  onEditSlotSuccess,
  selectedUser,
  emailExclusionList,
  setEmailExclusionList,
  onDeleteSlotSuccess,
  handleActionDelete,
  onNoiseChange,
  showBlockAction = true,
  showSourceIcon = false,
  highlightQuery = "",
}) => {
  const [hovered, setHovered] = useState(false);
  const [durationSaving, setDurationSaving] = useState(false);
  const [mobilePopupOpen, setMobilePopupOpen] = useState(false);
  const rowRef = useRef(null);
  const mobilePopupRef = useRef(null);

  const meta = slot?.metadata;
  const emailType = meta?.type;
  const participants = getOutlookParticipants(meta);
  const subject = meta?.activity_title || slot.sys_notes;
  const validLinks = (slot.external_links || []).filter(l => l?.url);
  const firstLink = validLinks[0];
  const startTime = slot.start_display || '';
  const participantsStr = participants.join(', ');
  const derivedSourceIcons = getItemSourceIcons(slot);
  const sourceItem = {
    ...slot,
    source_icons: derivedSourceIcons.length
      ? derivedSourceIcons
      : (slot.source_icons || []),
  };
  const getRelatedTimeEntryId = (item) => item?.relateTimeEntry?.id || item?.timeentry || item?.timeentry_id || item?.time_entry_id;
  const actionMenu = onDeleteSlotSuccess && handleActionDelete ? (
    <RenderDeleteSlot
      item={slot}
      isMobile={isMobile}
      selectedUser={selectedUser}
      emailExclusionList={emailExclusionList}
      setEmailExclusionList={setEmailExclusionList}
      onDeleteSlotSuccess={(options) => onDeleteSlotSuccess(slot, options)}
      handleActionDelete={(item, options) => {
        const relatedTimeEntryId = getRelatedTimeEntryId(item);
        if (!relatedTimeEntryId) return;
        return handleActionDelete([relatedTimeEntryId], options);
      }}
      onEditSlotSuccess={onEditSlotSuccess}
      onNoiseChange={onNoiseChange}
      showBlockAction={showBlockAction}
    />
  ) : null;

  useEffect(() => {
    if (!mobilePopupOpen) return;
    const close = (e) => {
      if (!mobilePopupRef.current?.contains(e.target) && !rowRef.current?.contains(e.target)) {
        setMobilePopupOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [mobilePopupOpen]);

  const tooltipLines = buildSlotTooltipLines(meta, subject);
  const truncate = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' };

  if (isMobile) {
    return (
      <div
        ref={rowRef}
        className={isNoiseItem(slot) ? "noise-item" : undefined}
        style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '5px 0', borderBottom: isLast ? 'none' : '1px solid #D9D9D9', cursor: tooltipLines ? 'pointer' : 'default' }}
        onClick={(e) => { e.stopPropagation(); if (tooltipLines) setMobilePopupOpen(p => !p); }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, width: '100%' }}>
          {showSourceIcon && <RenderSourceIcons item={sourceItem} muted={isNoiseItem(slot)} disableClockingClick />}
          <span style={{ fontWeight: 600, fontSize: 14, color: isNoiseItem(slot) ? '#999' : '#333', flexShrink: 0 }}>{startTime}</span>
          {emailType && <EmailTypeBadge emailType={emailType} />}
          <span style={{ ...truncate, flex: '1 1 0', minWidth: 0, fontSize: 14, color: isNoiseItem(slot) ? '#999' : '#333', fontWeight: 600 }}><HighlightedText text={participantsStr} query={highlightQuery} /></span>
          {actionMenu}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, width: '100%' }}>
          <span style={{ color: isNoiseItem(slot) ? '#999' : '#333', fontSize: 14, flex: '0 1 auto', minWidth: 70, display: 'inline-flex', alignItems: 'center' }}>
              <RenderDurationText slot={slot} applyMinIncrement={applyMinIncrement} entryIsRunningClocking={entryIsRunningClocking} editable onEditSlotSuccess={onEditSlotSuccess} onSavingChange={setDurationSaving} durationSaving={durationSaving} isNoise={isNoiseItem(slot)} editorGap={4} editLayout="inline" alwaysShowEditIcon editIconPosition="left" textStyle={{ direction: "ltr" }}/>
              {durationSaving && <DurationSavingSkeleton />}
          </span>
          <span style={{ ...truncate, flex: '0 1 auto', minWidth: 0, fontSize: 14, color: isNoiseItem(slot) ? '#999' : '#666', fontWeight: 400 }}><HighlightedText text={subject} query={highlightQuery} /></span>
          {firstLink && (
            <a
              style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', textDecoration: 'none', marginLeft: 0 }}
              href={firstLink.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={HYPER_LINK} alt="" style={{ width: 16, height: 16 }} />
            </a>
          )}
        </div>
        {mobilePopupOpen && tooltipLines && (
          <MobileEmailPopup anchor={rowRef.current} popupRef={mobilePopupRef} lines={tooltipLines} />
        )}
      </div>
    );
  }

  const rowContent = (
    <div
      style={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, backgroundColor: hovered? '#FFFFFF33': '', borderRadius: 4, cursor: firstLink ? 'pointer' : 'default' }}
      onClick={() => firstLink && window.open(firstLink.url, '_blank', 'noopener,noreferrer')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
        {showSourceIcon && <RenderSourceIcons item={sourceItem} muted={isNoiseItem(slot)} disableClockingClick />}
        <span style={{ fontWeight: 600, fontSize: 14, color: isNoiseItem(slot) ? '#999' : '#333', flex: '0 0 70px' }}>{startTime}</span>
        {emailType && <EmailTypeBadge emailType={emailType} />}
        <span style={{ ...truncate, flex: '1 1 0', minWidth: 0, fontSize: 14, fontWeight: 600, color: isNoiseItem(slot) ? '#999' : '#333', textDecoration: hovered ? 'underline' : 'none' }}><HighlightedText text={participantsStr} query={highlightQuery} /></span>
        {showSourceIcon && (
          <span style={{ flexShrink: 0, fontSize: 14, fontWeight: 600, color: isNoiseItem(slot) ? '#999' : '#333' }}>
            <RenderDurationText slot={slot} applyMinIncrement={applyMinIncrement} entryIsRunningClocking={entryIsRunningClocking} editable onEditSlotSuccess={onEditSlotSuccess} onSavingChange={setDurationSaving} durationSaving={durationSaving} isNoise={isNoiseItem(slot)} editorGap={4} editLayout="inline" editIconPosition="left" textStyle={{ direction: "ltr" }}/>
            {durationSaving && <DurationSavingSkeleton />}
          </span>
        )}
        {showSourceIcon && actionMenu}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, minWidth: 0, overflow: 'hidden', paddingLeft: showSourceIcon ? 32 : 0 }}>
        {!showSourceIcon && (
          <span style={{ fontSize: 14, color: isNoiseItem(slot) ? '#999' : '#333', flex: '0 1 auto', minWidth: 70 }}>
            <RenderDurationText slot={slot} applyMinIncrement={applyMinIncrement} onSavingChange={setDurationSaving} durationSaving={durationSaving} entryIsRunningClocking={entryIsRunningClocking} editable onEditSlotSuccess={onEditSlotSuccess} isNoise={isNoiseItem(slot)} editorGap={4} editIconPosition="left" textStyle={{ direction: "ltr"}}/>
            {durationSaving && <DurationSavingSkeleton />}
          </span>
        )}
        <span style={{ ...truncate, flex: '1 1 0', minWidth: 0, fontSize: 14, fontWeight: 400, color: isNoiseItem(slot) ? '#999' : '#666', textDecoration: hovered ? 'underline' : 'none' }}><HighlightedText text={subject} query={highlightQuery} /></span>
        {!showSourceIcon && actionMenu}
      </div>
    </div>
  );

  return (
    <div
      ref={rowRef}
      className={isNoiseItem(slot) ? "noise-item" : undefined}
      style={{ padding: '6px 0', borderBottom: isLast ? 'none' : `1px solid ${hovered ? '#FFF' : '#D9D9D9'}` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {rowContent}
      {hovered && tooltipLines && rowRef.current && (
        <Popup
          anchor={rowRef.current}
          show={true}
          anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'left', vertical: 'top' }}
          style={{ zIndex: 9999 }}
        >
          <div style={{ ...OUTLOOK_TOOLTIP_STYLE, borderRadius: 4 }}>
            <TooltipLinesList lines={tooltipLines} />
          </div>
        </Popup>
      )}
    </div>
  );
};

// ── Entry / slot description for Outlook emails (used by RenderSysNotes) ──
const OutlookEmailNotes = ({ item, type, isMobile, noExternalLink = false, selectedUser, muted = false, highlightQuery = "", emailSubjectOffset = 0, emailSummaryPart }) => {
  const [hovered, setHovered] = useState(false);
  const [mobilePopupOpen, setMobilePopupOpen] = useState(false);
  const wrapperRef = useRef(null);
  const mobilePopupRef = useRef(null);

  const validLinks = noExternalLink ? [] : (item.external_links || []).filter(l => l?.url);
  const firstLink = validLinks[0];

  useEffect(() => {
    if (!mobilePopupOpen) return;
    const close = (e) => {
      if (!mobilePopupRef.current?.contains(e.target) && !wrapperRef.current?.contains(e.target)) {
        setMobilePopupOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [mobilePopupOpen]);

  // ── display content ──
  let badge = null;
  let firstLineText = '';
  let subject = null;
  let hasFirstLine = false;
  let allParticipants = [];

  if (type === 'entry') {
    const slots = item.slots || [];
    subject = item?.metadata?.activity_title;

    if (slots.length > 1) {
      const emailTypes = [
        ...new Set(
          slots
            .map(slot => String(slot?.metadata?.type || '').trim())
            .filter(Boolean)
        ),
      ];
      if (emailTypes.length === 1) badge = emailTypes[0];

      const slotSubjects = [
        ...new Set(
          slots
            .map(slot => String(slot?.metadata?.activity_title || '').trim())
            .filter(Boolean)
        ),
      ];
      if (!subject && slotSubjects.length > 0) subject = slotSubjects[0];

      const seen = new Set();
      const selfAliases = getSelfParticipantAliases(item, selectedUser);
      slots.forEach(slot => {
        getOutlookParticipants(slot?.metadata).forEach(name => {
          if (isSelfParticipant(name, selfAliases)) return;
          if (!seen.has(name)) { seen.add(name); allParticipants.push(name); }
        });
      });
      firstLineText = allParticipants.join(', ');
      hasFirstLine = allParticipants.length > 0;
    } else {
      const slot = slots[0];
      const meta = slot?.metadata || item?.metadata;
      subject = meta?.activity_title || subject;
      if (meta?.type) {
        badge = meta.type;
        firstLineText = getOutlookParticipants(meta).join(', ');
        hasFirstLine = true;
      }
    }
  } else {
    const meta = item?.metadata;
    subject = meta?.activity_title || item.sys_notes;
    if (meta?.type) {
      badge = meta.type;
      firstLineText = getOutlookParticipants(meta).join(', ');
      hasFirstLine = true;
    }
  }

  // ── tooltip ──
  const tooltipLines = (() => {
    const slots = item.slots || [];
    if (type === 'entry' && slots.length > 1) {
      const representativeMeta = slots.find(slot => slot?.metadata)?.metadata;
      if (!representativeMeta) return subject ? [{ label: 'Subject', value: subject }] : null;

      const contacts = [];
      const seenContacts = new Set();
      slots.forEach(slot => {
        (slot?.metadata?.contacts || []).forEach(contact => {
          const key = `${contact?.role || ''}:${contact?.email || contact?.name || ''}`;
          if (!seenContacts.has(key)) {
            seenContacts.add(key);
            contacts.push(contact);
          }
        });
      });
      return buildSlotTooltipLines(
        { ...representativeMeta, type: badge || representativeMeta.type, contacts },
        subject
      );
    }
    const meta = type === 'slot' ? item?.metadata : (slots[0]?.metadata || item?.metadata);
    return buildSlotTooltipLines(meta, subject);
  })();

  const lineStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' };
  const underline = hovered && !isMobile ? 'underline' : 'none';

  const primaryColor = muted ? '#999' : '#333';
  const secondaryColor = muted ? '#999' : '#525252';

  const innerContent = (
    <>
      {hasFirstLine && emailSummaryPart !== 'subject' && (
        <div
          data-email-summary-first-line
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            overflow: 'hidden',
            width: '100%',
            minHeight: emailSubjectOffset ? 24 : undefined,
          }}
        >
          {badge && <EmailTypeBadge emailType={badge} />}
          <span
            data-email-summary-participant
            style={{
              ...lineStyle,
              flex: 1,
              color: primaryColor,
              fontSize: 14,
              textDecoration: underline,
              fontWeight: 600,
              transform: emailSubjectOffset ? 'translateY(2px)' : undefined,
            }}
          >
            <HighlightedText text={firstLineText} query={highlightQuery} />
          </span>
        </div>
      )}
      {subject && emailSummaryPart !== 'participants' && (
        <div
          data-email-subject-aligned-with-source={hasFirstLine && emailSubjectOffset ? true : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            minWidth: 0,
            position: hasFirstLine && emailSubjectOffset ? 'relative' : undefined,
            left: hasFirstLine && emailSubjectOffset ? -emailSubjectOffset : undefined,
            width: hasFirstLine && emailSubjectOffset
              ? `calc(100% + ${emailSubjectOffset}px)`
              : undefined,
          }}
        >
          <div style={{ ...lineStyle, color: secondaryColor, fontSize: 14, marginTop: hasFirstLine && emailSummaryPart !== 'subject' ? 2 : 0, textDecoration: underline, minWidth: 0, flex: '0 1 auto', maxWidth: isMobile && firstLink ? 'calc(100% - 24px)' : 'calc(100% - 28px)' }}>
            <HighlightedText text={subject} query={highlightQuery} />
          </div>
          {isMobile && firstLink && (
            <a
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                textDecoration: "none",
                marginTop: hasFirstLine ? 2 : 0,
              }}
              href={firstLink.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={HYPER_LINK} alt="" style={{ width: 16, height: 16 }} />
            </a>
          )}
        </div>
      )}
    </>
  );
  
  return (
    <div
      ref={wrapperRef}
      style={{ display: 'flex', width: '100%', alignItems: 'flex-start', gap: 4 }}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
    >
      <div
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, cursor: tooltipLines || firstLink ? 'pointer' : 'default',  }}
        onClick={(e) => {
          if (isMobile && tooltipLines) {
            e.stopPropagation();
            setMobilePopupOpen(p => !p);
          } else if (!isMobile && firstLink) {
            window.open(firstLink.url, '_blank', 'noopener,noreferrer');
          }
        }}
      >
        {innerContent}        
      </div>

      {isMobile && mobilePopupOpen && tooltipLines && (
        <MobileEmailPopup anchor={wrapperRef.current} popupRef={mobilePopupRef} lines={tooltipLines} />
      )}

      {!isMobile && hovered && tooltipLines && wrapperRef.current && (
        <Popup
          anchor={wrapperRef.current}
          show={true}
          anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'left', vertical: 'top' }}
          style={{ zIndex: 9999 }}
        >
          <div style={{ ...OUTLOOK_TOOLTIP_STYLE, borderRadius: 4 }}>
            <TooltipLinesList lines={tooltipLines} />
          </div>
        </Popup>
      )}
    </div>
  );
};

export const RenderSysNotes = ({ item, type = "entry", isMobile = false, noExternalLink = false, selectedUser, muted = false, highlightQuery = "", emailSubjectOffset = 0, emailSummaryPart = /** @type {'participants' | 'subject' | undefined} */ (undefined), webLabelMode = /** @type {'matterEntry' | 'matterSlot' | 'timeSlot' | 'activity' | 'activityGroup' | undefined} */ (undefined) }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const popupRef = useRef(null);
  const validLinks = (item.external_links || []).filter(link => link?.url);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        popupAnchor &&
        !popupAnchor.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, popupAnchor]);

  // Email activity: delegate to dedicated component. Keep this return after
  // every hook so reused timeline rows always execute the same hook sequence.
  if (isEmailActivityItem(item) && !isReminderCalendarItem(item)) {
    return <OutlookEmailNotes item={item} type={type} isMobile={isMobile} noExternalLink={noExternalLink} selectedUser={selectedUser} muted={muted || isNoiseItem(item)} highlightQuery={highlightQuery} emailSubjectOffset={emailSubjectOffset} emailSummaryPart={emailSummaryPart} />;
  }

  if (shouldUseWebActivityLabel(item, webLabelMode)) {
    return (
      <WebActivityNotes
        item={item}
        mode={webLabelMode}
        isMobile={isMobile}
        muted={muted || isNoiseItem(item)}
        highlightQuery={highlightQuery}
      />
    );
  }

  const hasMultipleLinks = validLinks.length > 1;

  const handleClick = (e) => {
    if (!validLinks || validLinks.length === 0) return;

    const targetLink = validLinks[0];

    window.open(
      targetLink.url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const documentsSources = [
    "Word", "Excel", "PowerPoint", "PDF", "Office",
    "Windows Agent", "Bmp", "Csv", "Doc", "Doct", "Docx", "Dxl",
    "Eml", "Gif", "Html", "Jpeg", "Jpg", "Json", "Lfd", "Msg",
    "One", "Qpw", "Pcx", "Pdf", "Plain", "Png", "Ppt", "Pptx",
    "Pub", "Rtf", "Shw", "Tif", "Tiff", "Txt", "Url", "Vsd",
    "Wmf", "Wpd", "Xls", "Xlsx", "Xml", "Zip", "Avi", "Mov",
    "Mp3", "Mp4", "Wav"
  ];


  const getDisplayText = () => {
    const firstApp = item.apps?.[0] || item.non_svc_source || "";
    const source = sourceNameMapping[firstApp] || firstApp;

    if (source === "MIRA" || source === "Clocking") {
      const narrative = item.description || item.sys_notes || item.narrative || getWorkedOnText(item.worked_on) || "";
      return { text: narrative, isNarrative: true, isWorkedOn: false };
    }

    if (documentsSources.includes(source)) {
      return type === "entry"
        ? { text: getWorkedOnText((item.worked_on?.replace(/\.eml$/i, "") || "")), isNarrative: false, isWorkedOn: true, }
        : { text: item.sys_notes, isNarrative: false, isWorkedOn: false };
    }

    switch (source) {
      case "Outlook":
        return type === "entry"
          ? { text: item?.metadata?.activity_title, isNarrative: false, isWorkedOn: false, }
          : { text: item.sys_notes, isNarrative: false, isWorkedOn: false};
      case "Meeting":
      case "Teams":
      case "Zoom":
        return { text: item.worked_on, isNarrative: false, isWorkedOn: false};
      case "Browser":
        return { text: getWorkedOnText(item.worked_on), isNarrative: false, isWorkedOn: true,};
      case "iManage":
        const sys = item.activity || "";
        const worked = item.worked_on ? getWorkedOnText(item.worked_on) : "";
        const combined = sys && worked ? `${sys} ${worked}` : sys || worked;
        return { text: combined, isNarrative: false, isWorkedOn: false };
      default:
        const fallback = item.sys_notes || getWorkedOnText(item.worked_on) || item.narrative || item.description || "";
        return { text: fallback, isNarrative: false, isWorkedOn: false };
    }
  };

  const { text: displayText, isWorkedOn, isNarrative } = getDisplayText();

  if (isNarrative) {
    return  (
      <div style={{ display: "flex", width: "100%", alignItems: "center" }}>
        <div style={{ minWidth: 0 }}>
          <Tooltip
                tooltipStyle={{
                    maxWidth: "200px",
                    border: "none",
                    backgroundColor: "#000",
                    color: "#fff",
                    padding: "4px 8px",
                    fontSize: "14px",
                    fontWeight: 400,
                }}
                anchorElement="target"
                position="top"
            >
              <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                        color: muted || isNoiseItem(item) ? "#999" : "#525252",
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                      title={`Narrative: ${NarrativeStr(displayText, item)}`}
                    >
                      {RenderNarrative(displayText, item?.metadata || {}, false, item, highlightQuery)}
              </div>
          </Tooltip>
        </div>
      </div>
    )
  }

  const TooltipContentTemplate = props => {
    if (isWorkedOn) {
        return <div style={{ whiteSpace: "normal", wordBreak: "break-word" }} dangerouslySetInnerHTML={{ __html: props.title }} /> 
    }else {
        return <div>{props.title}</div>;
    }

  };

  return (
    <div
      style={{ display: "flex", width: "100%", alignItems: "center" }}
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
    >
      <div style={{ minWidth: 0 }}>
        <Tooltip
          anchorElement="target"
          position="top"
          tooltipStyle={{
            maxWidth: "200px",
            border: "none",
            backgroundColor: "#000",
            color: "#fff",
            padding: "4px 8px",
            fontSize: "14px",
            fontWeight: 400,
          }}
          content={props => <TooltipContentTemplate title={props.title} />}
        >
          <div
            data-tooltip-target
            onClick={handleClick}
            className={validLinks?.length ? "clickable-sysnotes" : ""}
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "block",
              color: muted || isNoiseItem(item) ? "#999" : "#525252",
              fontSize: 14,
              fontWeight: 400,
              cursor: validLinks?.length ? "pointer" : "default",
            }}
            title={isWorkedOn ?  (item.worked_on?.replace(/\.eml$/i, "") || "") : displayText}
          >
            <HighlightedText text={displayText} query={highlightQuery} />
          </div>
        </Tooltip>
      </div>

      {validLinks && validLinks.length >= 1 && (isMobile || hovered) && (
          <a
            style={{
              marginLeft: 4,
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
            }}
            href={validLinks[0].url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()} // 防止冒泡到文本 click
          >
            <img src={HYPER_LINK} />
          </a>
      )}

      {hasMultipleLinks && open && popupAnchor && (
        <Popup anchor={popupAnchor} show={open} className="sysnotes-popup">
          <div
            ref={popupRef}
            style={{
              background: "#fff",
              borderRadius: 4,
              padding: "8px 0px",
              maxHeight: "200px",
              overflowY: "scroll",
            }}
          >
            {validLinks.map((link, idx) => (
              <div
                key={idx}
                className="sysnotes-menu-item"
                onClick={() => {
                  window.open(link.url, "_blank", "noopener,noreferrer");
                  setOpen(false);
                }}
              >
                <img src={sourceIconMapping[link.key]} width={17} height={17} />
                <span style={{ flex: 1 }}>Open Link</span>
                <img src={HYPER_LINK} />
              </div>
            ))}
          </div>
        </Popup>
      )}
    </div>
  );
};

export const RenderParticipants = ({ item}) => {
  const [open, setOpen] = useState(false);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const popupRef = useRef(null);
  const closePopupTimeoutRef = useRef(null);

  // ✅ 1. 收集 & 去重
  const allParticipants = (item.external_links || []).flatMap(
    (l) => l.participants || []
  );

  const uniqueParticipants = Array.from(
    new Map(allParticipants.map((p) => [p.username, p])).values()
  );

  // ✅ 2. 是否包含自己
  const hasSelf = uniqueParticipants.some(
    (p) => p.username === item.tkpr_username
  );

  // ✅ 3. 排序：自己永远最后
  const sortedParticipants = [...uniqueParticipants].sort((a, b) => {
    if (a.username === item.tkpr_username) return 1;
    if (b.username === item.tkpr_username) return -1;
    return 0;
  });


  const clearClosePopupTimeout = () => {
    if (closePopupTimeoutRef.current) {
      clearTimeout(closePopupTimeoutRef.current);
      closePopupTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = (e) => {
    if (sortedParticipants.length <= 1) return;
    clearClosePopupTimeout();
    setPopupAnchor(e.currentTarget);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    clearClosePopupTimeout();
    closePopupTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closePopupTimeoutRef.current = null;
    }, 200);
  };

  // ✅ 4. 点击外部关闭 popup
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        popupAnchor &&
        !popupAnchor.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, popupAnchor]);
 
  useEffect(() => {
    return () => clearClosePopupTimeout();
  }, []);

  // Keep this after all hooks so every render calls hooks in the same order
  // ✅ 5. 只有自己 → 不显示 
  if (sortedParticipants.length === 1 && hasSelf) {
    return null;
  }

  // ✅ 6. 前面显示最多2个，其余进 +N
  const visibleParticipants = sortedParticipants.length > 2 ?  sortedParticipants.slice(0, 1) : sortedParticipants;
  const extraParticipants = sortedParticipants.length > 2 ? sortedParticipants.slice(1) : [];
  const extraCount = sortedParticipants.length > 2 ? sortedParticipants.length - 1 : 0;

  return (
    <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {/* 👤 头像叠加 */}
        {visibleParticipants.map((p, idx) => (
          <Tooltip
            key={p.username}
            tooltipStyle={{
              maxWidth: "200px",
              border: "none",
              backgroundColor: "#000",
              color: "#fff",
              padding: "4px 8px",
              fontSize: "14px",
              fontWeight: 400,
            }}
            anchorElement="target"
            position="left"
          >
            <img
              title={p.name}
              src={avatarURL(`/${p.avatar}`)}
              style={{
                width: 22,
                height: 22,
                boxSizing: "border-box",
                verticalAlign: "sub",
                borderRadius: "50%",
                marginLeft: idx === 0 ? 0 : -4,
                zIndex: visibleParticipants.length - idx,
                cursor: "pointer",
              }}
            />
          </Tooltip>
        ))}

        {/* ➕ +N */}
        {extraCount > 0 && (
          <div
            className="extraCount"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
              marginLeft: -4,
              width: 22,
              height: 22,
              boxSizing: "border-box",
              border: "1px solid #fff",
              borderRadius: "50%",
              color: "#865BDF",
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            +{extraCount}
          </div>
        )}
      </div>

      {/* 📦 Popup */}
      {extraCount > 0 && open && popupAnchor && (
        <Popup
          anchor={popupAnchor}
          anchorAlign={{ horizontal: "left", vertical: "bottom" }}
          popupAlign={{ horizontal: "right", vertical: "top" }}
          style={{ transform: 'translateX(20px) translateY(10px)' }}
          show={open} 
          className="sysnotes-popup"
        >
          <div
            ref={popupRef}
            onMouseEnter={clearClosePopupTimeout}
            onMouseLeave={handleMouseLeave}
            style={{
              minWidth: 160,
              maxWidth: 280,
              background: "#fff",
              borderRadius: 4,
              padding: "4px 0",
              maxHeight: 200,
              overflowY: "scroll",
            }}
          >
            {extraParticipants.map((p) => (
              <div
                key={p.username}
                className="participants-menu-item"
                style={{
                  padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center", 
                  gap: 8,
                }}
              >
                <img
                  src={avatarURL(`/${p.avatar}`)}
                  style={{ width: 24, height: 24, borderRadius: "50%" }}
                />
                <span style={{ flex: 1, wordWrap: "break-word", overflow: 'overlay' }}>{p.name}</span>
              </div>
            ))}
          </div>
        </Popup>
      )}
    </div>
  );
};

/** ================= EDIT DIALOG ================= */
const EditTimeSlotDialog = ({ open, item, onClose, onSubmit }) => {
  const TIMEFORMAT = "HH:mm:ss";

  if (!open) return null;

  const initialStart = item.start ? moment(item.start).toDate() : null;
  const initialStop = item.stop ? moment(item.stop).toDate() : null;
    // 根据 start 和 stop 自动计算 duration（小时）
  const initialDuration = calculateInitialDuration(initialStart, initialStop, item.duration)
  const initialDurationParts = splitDurationParts(initialDuration);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const secondRef = useRef(null);
  const [durationHours, setDurationHours] = useState(initialDurationParts.hours);
  const [durationMinutes, setDurationMinutes] = useState(initialDurationParts.minutes);
  const [durationSeconds, setDurationSeconds] = useState(initialDurationParts.seconds);
  const [start, setStart] = useState(initialStart);
  const [stop, setStop] = useState(initialStop);
  const [, forceDurationInputSync] = useState(0);
  const [durationInputVersions, setDurationInputVersions] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const pendingDurationFocusFieldRef = useRef(null);
  useSyncClampedNumericTextBoxValue(hourRef, durationHours, 0);
  useSyncClampedNumericTextBoxValue(minuteRef, durationMinutes, 0);
  useSyncClampedNumericTextBoxValue(secondRef, durationSeconds, 0);

  const SECOND = 1000;

  const getTotalDurationSeconds = ({
    hours = durationHours,
    minutes = durationMinutes,
    seconds = durationSeconds
  } = {}) =>
    ((hours || 0) * 3600) + ((minutes || 0) * 60) + (seconds || 0);

  const applyDurationSeconds = (nextStart, nextDurationSeconds) => {
    const clampedSeconds = clampDurationSecondsToStartDay(nextDurationSeconds, nextStart);
    const nextParts = splitDurationParts(clampedSeconds / 3600);

    setDurationHours(nextParts.hours);
    setDurationMinutes(nextParts.minutes);
    setDurationSeconds(nextParts.seconds);

    if (nextStart && clampedSeconds > 0) {
      setStop(new Date(nextStart.getTime() + clampedSeconds * SECOND));
    }
  };

  const syncStop = (nextStart, nextDurationSeconds) => {
    applyDurationSeconds(nextStart, nextDurationSeconds);
  };

  const getMaxDurationParts = () => splitDurationParts(getMaxDurationSecondsForStart(start) / 3600);
  const getMaxDurationPartValue = (field) => {
    const maxParts = getMaxDurationParts();
    if (field === "hours") return maxParts.hours;
    if (field === "minutes") return durationHours >= maxParts.hours ? maxParts.minutes : 59;
    if (field === "seconds") {
      return durationHours >= maxParts.hours && durationMinutes >= maxParts.minutes
        ? maxParts.seconds
        : 59;
    }
    return 59;
  };

  const handleStartChange = (e) => {
    const val = e.value;
    setStart(val);
    syncStop(val, getTotalDurationSeconds());
  };

  const handleHoursChange = (e) => {
    const val = normalizeDurationPart(e.value, getMaxDurationPartValue("hours"));
    applyDurationSeconds(start, getTotalDurationSeconds({ hours: val }));
    forceDurationInputSync(version => version + 1);
  };

  const handleMinutesChange = (e) => {
    const val = normalizeDurationPart(e.value, getMaxDurationPartValue("minutes"));
    applyDurationSeconds(start, getTotalDurationSeconds({ minutes: val }));
    forceDurationInputSync(version => version + 1);
  };

  const handleSecondsChange = (e) => {
    const val = normalizeDurationPart(e.value, getMaxDurationPartValue("seconds"));
    applyDurationSeconds(start, getTotalDurationSeconds({ seconds: val }));
    forceDurationInputSync(version => version + 1);
  };

  const forceDurationPartToMax = (field, max) => {
    if (field === "hours") {
      applyDurationSeconds(start, getTotalDurationSeconds({ hours: max }));
    } else if (field === "minutes") {
      applyDurationSeconds(start, getTotalDurationSeconds({ minutes: max }));
    } else {
      applyDurationSeconds(start, getTotalDurationSeconds({ seconds: max }));
    }

    forceDurationInputSync(version => version + 1);
    pendingDurationFocusFieldRef.current = field;
    setDurationInputVersions(prev => ({ ...prev, [field]: prev[field] + 1 }));
  };

  useEffect(() => {
    const bindImmediateMax = (field, ref) => {
      const input = ref.current?.element;
      if (!input) return undefined;

      const getNextValue = (event) => {
        if (!event.data) return null;

        const currentValue = String(input.value || "");
        const selectionStart = input.selectionStart ?? currentValue.length;
        const selectionEnd = input.selectionEnd ?? selectionStart;

        return `${currentValue.slice(0, selectionStart)}${event.data}${currentValue.slice(selectionEnd)}`;
      };

      const handleBeforeInput = (event) => {
        const nextValue = getNextValue(event);
        if (nextValue == null) return;

        const max = getMaxDurationPartValue(field);
        const nextNumber = Number(nextValue.replace(/,/g, ""));
        if (!Number.isFinite(nextNumber) || nextNumber <= max) return;

        event.preventDefault();
        forceDurationPartToMax(field, max);
      };

      const handleInput = () => {
        const max = getMaxDurationPartValue(field);
        const rawValue = Number(String(input.value).replace(/,/g, ""));
        if (!Number.isFinite(rawValue) || rawValue <= max) return;

        forceDurationPartToMax(field, max);
      };

      input.addEventListener("beforeinput", handleBeforeInput, true);
      input.addEventListener("input", handleInput, true);
      return () => {
        input.removeEventListener("beforeinput", handleBeforeInput, true);
        input.removeEventListener("input", handleInput, true);
      };
    };

    const cleanups = [
      bindImmediateMax("hours", hourRef),
      bindImmediateMax("minutes", minuteRef),
      bindImmediateMax("seconds", secondRef),
    ];

    return () => cleanups.forEach(cleanup => cleanup?.());
  }, [start, durationHours, durationMinutes, durationSeconds]);

  const handleDurationFocus = (ref) => {
    ref.current?.element?.select();
  };

  const handleDurationBlur = (ref) => {
    setTimeout(() => formatDurationInputDisplay(ref), 0);
  };

  useLayoutEffect(() => {
    if (!pendingDurationFocusFieldRef.current) return;

    const focusRef = {
      hours: hourRef,
      minutes: minuteRef,
      seconds: secondRef,
    }[pendingDurationFocusFieldRef.current];
    pendingDurationFocusFieldRef.current = null;

    const input = focusRef.current?.element;
    input?.focus?.();
    const cursorPosition = input?.value?.length ?? 0;
    input?.setSelectionRange?.(cursorPosition, cursorPosition);
  });

  const isDisabled = !start || !stop || getTotalDurationSeconds() <= 0;

  const formatUTC = (t) =>
    moment(t).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[000]") + "Z";


  useEffect(() => {
    const addEnterFocus = (sourceRef, targetRef) => {
      const input = sourceRef.current?.element;
      if (!input) return null;

      const handleKeyDown = (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        formatDurationInputDisplay(sourceRef, true);
        targetRef.current?.focus?.();
        targetRef.current?.element?.focus?.();
        targetRef.current?.element?.select();
      };

      input.addEventListener("keydown", handleKeyDown);
      return () => input.removeEventListener("keydown", handleKeyDown);
    };

    const removeHourListener = addEnterFocus(hourRef, minuteRef);
    const removeMinuteListener = addEnterFocus(minuteRef, secondRef);

    return () => {
      removeHourListener && removeHourListener();
      removeMinuteListener && removeMinuteListener();
    };
  }, []);

  useEffect(() => {
    formatDurationInputDisplay(hourRef);
    formatDurationInputDisplay(minuteRef);
    formatDurationInputDisplay(secondRef);
  }, [durationHours, durationMinutes, durationSeconds]);


  return createPortal(
    <div style={overlayStyle} 
    onMouseMove={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    onMouseUp={(e) => e.stopPropagation()}
    onClick={(e) => e.stopPropagation()}>
      <div style={modalStyle}>
        {/* Duration */}
        <div style={blockStyle}>
          <div style={labelStyle}><span style={requireStyle}>*</span>Duration</div>
          <div style={durationRowStyle}>
            <div style={durationPartStyle}>
              <NumericTextBox
                key={`hours-${durationInputVersions.hours}`}
                ref={hourRef}
                className="edit-time-slot-duration-input"
                value={durationHours}
                min={0}
                max={getMaxDurationPartValue("hours")}
                step={1}
                format="n0"
                inputStyle={durationInputStyle}
                width="100%"
                onChange={handleHoursChange}
                onFocus={() => handleDurationFocus(hourRef)}
                onBlur={() => handleDurationBlur(hourRef)}
              />
              <span style={durationUnitStyle}>h</span>
            </div>

            <div style={durationPartStyle}>
              <NumericTextBox
                key={`minutes-${durationInputVersions.minutes}`}
                ref={minuteRef}
                className="edit-time-slot-duration-input"
                value={durationMinutes}
                min={0}
                max={getMaxDurationPartValue("minutes")}
                step={1}
                format="n0"
                inputStyle={durationInputStyle}
                width="100%"
                onChange={handleMinutesChange}
                onFocus={() => handleDurationFocus(minuteRef)}
                onBlur={() => handleDurationBlur(minuteRef)}
              />
              <span style={durationUnitStyle}>m</span>
            </div>

            <div style={durationPartStyle}>
              <NumericTextBox
                key={`seconds-${durationInputVersions.seconds}`}
                ref={secondRef}
                className="edit-time-slot-duration-input"
                value={durationSeconds}
                min={0}
                max={getMaxDurationPartValue("seconds")}
                step={1}
                format="n0"
                inputStyle={durationInputStyle}
                width="100%"
                onChange={handleSecondsChange}
                onFocus={() => handleDurationFocus(secondRef)}
                onBlur={() => handleDurationBlur(secondRef)}
              />
              <span style={durationUnitStyle}>s</span>
            </div>
          </div>
        </div>

        {/* Start */}
        <div style={blockStyle}>
          <div style={labelStyle}><span style={requireStyle}>*</span>Start</div>
          <TimePickerWithEnter
            value={start}
            onChange={handleStartChange}
            format={TIMEFORMAT}
          />
        </div>

        {/* Stop */}
        <div style={blockStyle}>
          <div style={labelStyle}><span style={requireStyle}>*</span>Stop</div>
          <TimePickerWithEnter
            value={stop}
            disabled
            format={TIMEFORMAT}
          />
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="confirm-button primary-button" disabled={isDisabled} onClick={() => {
            onSubmit({
              id: item.id,
              start: formatUTC(start),
              stop: formatUTC(stop)
            });
            onClose();
          }}>
            Confirm
          </button>

          <button  className="cancel-button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/** ================= STYLES ================= */
const menuItemStyle = {
  padding: "4px 10px",
  display: "flex",
  alignItems: "center",
  gap: 6,
  cursor: "pointer",
  color: "#333",
  userSelect: "none",
};

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(0,0,0,0.5)",
  zIndex: 10001
};

const modalStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "#fff",
  padding: 24,
  borderRadius: 12,
  width: 400,
  boxSizing: "border-box",
  zIndex: 10002,
  boxShadow: "0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.30)"
};

const blockStyle = {
  marginBottom: 20
};

const requireStyle = {
  color: "#E25750",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "16px"
};

const labelStyle = {
  color: "#525252",
  fontWeight: 400,
  fontSize: 12,
  lineHeight: "16px",
  letterSpacing: 0.5
};

const durationRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%"
};

const durationPartStyle = {
  display: "flex",
  alignItems: "center",
  flex: "1 1 0",
  gap: 4,
  minWidth: 0
};

const durationInputStyle = {
  color: "#6E687C",
  fontSize: 16,
  letterSpacing: 0.25,
  lineHeight: "20px",
  textAlign: "left"
};

const durationUnitStyle = {
  color: "#525252",
  fontSize: 14,
  lineHeight: "16px",
  letterSpacing: 0.5
};

const splitDurationParts = (durationHours) => {
  const totalSeconds = Math.max(0, Math.round((durationHours || 0) * 3600));

  return {
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60
  };
};

const normalizeDurationPart = (value, max) => {
  if (value == null) return null;

  return Math.max(0, Math.min(max, Math.trunc(value)));
};

const formatDurationInputDisplay = (ref, force = false) => {
  const input = ref.current?.element;
  if (!input || (!force && document.activeElement === input)) return;

  const value = input.value === "" ? 0 : Number(input.value);
  input.value = Number.isFinite(value)
    ? String(Math.trunc(value)).padStart(2, "0")
    : "00";
};

const calculateInitialDuration = (start, stop, fallbackDurationSec) => {
  const durationSec = start && stop ? (stop.getTime() - start.getTime()) / 1000 : fallbackDurationSec ?? 0;
  if (durationSec <= 0) return 0;

  return durationSec / 3600;
};

export const getLogicalStatus = (item) => {
    // 已经 released / discarded / releasing
    if (["released", "ready2release", "discarded"].includes(item.status)) {
        return item.status;
    }

    // fetched / created 需要看 sub_status
    if (item.status === "fetched" || item.status === "created") {
        return item.sub_status; // review_required / ready_to_release
    }

    return item.status;
};

export const TimePickerWithEnter = ({
  id,
  value,
  onChange,
  error,
  placeholder,
  format = 'HH:mm:ss',
  className,
  style,
  disabled = false
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const inputEl = document.getElementById(id);
    if (!inputEl) return;
    inputRef.current = inputEl;

    const handleKeyDown = (e) => {
      const val = inputEl.value || '';
      const parts = val.split(':');
      let currentPos = 0;
      const positions = parts.map((part) => {
        const start = currentPos;
        const end = start + part.length;
        currentPos = end + 1;
        return [start, end];
      });

      const pos = inputEl.selectionStart;

      if (e.key === 'Enter') {
        for (let i = 0; i < positions.length; i++) {
          const [start, end] = positions[i];
          if (pos <= end) {
            const nextIndex = (i + 1) % positions.length;
            const [nextStart, nextEnd] = positions[nextIndex];
            inputEl.setSelectionRange(nextStart, nextEnd);
            e.preventDefault();
            break;
          }
        }
      }
    };

    inputEl.addEventListener('keydown', handleKeyDown);

    return () => {
      inputEl.removeEventListener('keydown', handleKeyDown);
    };
  }, [id, onChange]);

  const combinedClassName = ['time-picker-with-enter', className, error ? 'form-error-border' : ''].filter(Boolean).join(' ');

  return (
    <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <TimePicker
        id={id}
        className={combinedClassName}
        value={value || null}
        format={format}
        onChange={onChange}
        placeholder={placeholder || format}
        style={{ width: '100%', ...style }}
        disabled={disabled}
      />
      {value && !disabled && (
        <span
          className="k-icon k-i-x"
          style={{
            position: 'absolute',
            right: 36,
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            color: '#999',
            zIndex: 1
          }}
          onClick={() => onChange({ value: null })}
        />
      )}
    </div>
  );
};

export const isSupportedBrowser = () => {
  const ua = navigator.userAgent;

  // 判断移动端
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
  if (isMobile) {
    return true; // 移动端全部支持
  }

  // 判断桌面客户端（Electron 的 Teams / Outlook 等）
  const isDesktopApp = ua.includes("Electron");
  if (isDesktopApp) {
    return true; // 桌面客户端全部支持
  }

  // 排除常见套壳浏览器
  const isShellBrowser =
    /Quark|QuarkPC|UCPC|UCBrowser|UBrowser|UCWEB|MQQBrowser|MetaSr|360Browser|QihooBrowser|OPR|SLbrowser/i.test(
      ua
    );

  if (isShellBrowser) {
    return false;
  }


  // Web 浏览器限制 Chrome / Edge
  const isEdge = ua.includes("Edg/");
  const isChrome = ua.includes("Chrome/") && !ua.includes("Edg/");

  return isEdge || isChrome;
};

export const MATTER_COLORS = [
  "#5F6369",
  "#17428C",
  "#2D84F5",
  "#2157B9",
  "#7F5CB8",
  "#F9654E",
  "#1A8039",
  "#389990",
  "#057E91",
  "#E97116",
];

export const MATTER_LIGHT_COLORS = [
  "#EFEFF0",
  "#E8ECF3",
  "#EAF3FE",
  "#E9EEF8",
  "#F2EFF8",
  "#FEF0ED",
  "#E8F2EB",
  "#EBF5F4",
  "#EBF5F6",
  "#FDF1E8",
];

const getMatterIdentityKey = matterId =>
  matterId === null || matterId === undefined ? null : String(matterId)

/**
 * Assign colors to a visible set in first-appearance order. Repeated matters
 * keep their first color and the palette only repeats after all colors are used.
 */
export const getOrderedMatterColorMap = matterIds => {
  const colors = new Map()
  for (const matterId of matterIds || []) {
    const key = getMatterIdentityKey(matterId)
    if (key === null || key === '' || colors.has(key)) continue
    colors.set(key, MATTER_COLORS[colors.size % MATTER_COLORS.length])
  }

  return colors
}

export const getMatterColor = matterId => {
  const key = getMatterIdentityKey(matterId)
  if (!key) return MATTER_COLORS[0]

  const index = Number(key[key.length - 1])
  return Number.isNaN(index) ? MATTER_COLORS[0] : MATTER_COLORS[index]
}

export const getMatterLightColor = (matterId) => {
  const key = getMatterIdentityKey(matterId)
  if (!key) return MATTER_LIGHT_COLORS[0]

  const index = Number(key[key.length - 1])
  return Number.isNaN(index) ? MATTER_LIGHT_COLORS[0] : MATTER_LIGHT_COLORS[index]
};

export const getMatterLightColorFromColor = (matterColor, matterId) => {
  const normalizedColor = String(matterColor || '').toUpperCase()
  const colorIndex = MATTER_COLORS.findIndex(
    color => color.toUpperCase() === normalizedColor
  )

  return colorIndex >= 0
    ? MATTER_LIGHT_COLORS[colorIndex]
    : getMatterLightColor(matterId)
}



export const useStatusWithLoading = (status, delay = 1000) => {
    const [uiStatus, setUIStatus] = useState(status);
    const prevStatusRef = useRef(status);

    useEffect(() => {
        // ✅ 如果状态没变，直接跳过（避免闪烁）
        if (prevStatusRef.current === status) return;

        // ✅ 状态变化 → 先进入 loading
        setUIStatus('loading');

        const timer = setTimeout(() => {
        setUIStatus(status);
        prevStatusRef.current = status;
        }, delay);

        return () => clearTimeout(timer);
    }, [status, delay]);

    return uiStatus;
};

const hoverActionPopupClosers = new Set();
const HOVER_ACTION_POPUP_CLOSE_DELAY = 150;

const closeOtherHoverActionPopups = (currentClose) => {
    hoverActionPopupClosers.forEach((close) => {
        if (close !== currentClose) close();
    });
};

const isScrollableOverflow = (value) => /auto|scroll|overlay/.test(value || "");

const getScrollableAncestors = (element) => {
    const ancestors = [];
    let node = element?.parentElement;
    while (node) {
        const style = window.getComputedStyle(node);
        if (
            isScrollableOverflow(style.overflowY) ||
            isScrollableOverflow(style.overflowX) ||
            isScrollableOverflow(style.overflow)
        ) {
            ancestors.push(node);
        }
        if (node === document.body || node === document.documentElement) break;
        node = node.parentElement;
    }
    return ancestors;
};

/**
 * @param {{
 *   icon: string,
 *   content: React.ReactNode,
 *   onAction?: (() => void) | null,
 *   actionLabel?: string,
 *   actionIcon?: string,
 *   openDelay?: number,
 *   actionContent?: React.ReactNode,
 *   hideIcon?: boolean,
 *   closeOnAction?: boolean,
 * }} props
 */
export const HoverActionPopup = ({
    icon,
    content,
    onAction,
    actionLabel = "Action",
    actionIcon,
    openDelay = 0,
    actionContent,
    hideIcon = false,
    closeOnAction = false,
}) => {
    const [showPopup, setShowPopup] = useState(false);
    const anchorRef = useRef(null);
    const triggerRef = useRef(null);
    const popupContentRef = useRef(null);
    const popupEnteredRef = useRef(false);
    const showTimer = useRef(null);
    const closeTimer = useRef(null);
    const unmountTimer = useRef(null);
    const closeSelfRef = useRef(null);

    const cancelScheduledClose = () => {
        clearTimeout(closeTimer.current);
        clearTimeout(unmountTimer.current);
    };

    const closePopupAndCommit = (delay = 0) => {
        cancelScheduledClose();
        closeTimer.current = window.setTimeout(() => {
            const activeElement = document.activeElement;
            if (
                activeElement &&
                popupContentRef.current?.contains(activeElement) &&
                typeof activeElement.blur === "function"
            ) {
                activeElement.blur();
            }

            // InlineDurationEditor also defers its blur-save by one tick. Keep
            // the popup mounted until that save handler has had a chance to run.
            unmountTimer.current = window.setTimeout(() => {
                setShowPopup(false);
            }, 0);
        }, delay);
    };

    const closePopupImmediately = () => {
        clearTimeout(showTimer.current);
        cancelScheduledClose();
        setShowPopup(false);
    };

    const openPopup = () => {
        cancelScheduledClose();
        closeOtherHoverActionPopups(closeSelfRef.current);
        if (!showPopup) popupEnteredRef.current = false;
        setShowPopup(true);
    };

    const handlePopupEnter = () => {
        // Opening on icon hover alone is passive. Moving into the black popup
        // is an intentional interaction, so its duration editor should commit
        // when this popup later closes.
        popupEnteredRef.current = true;
        cancelScheduledClose();
    };

    const handleEnter = () => {
        clearTimeout(showTimer.current);
        cancelScheduledClose();

        if (openDelay > 0 && !showPopup) {
            showTimer.current = setTimeout(() => {
                openPopup();
            }, openDelay);
            return;
        }

        openPopup();
    };

    const handleClick = (e) => {
        e.stopPropagation();
        clearTimeout(showTimer.current);
        openPopup();
    };

    const handleLeave = () => {
        clearTimeout(showTimer.current);
        if (showPopup) {
            // Allow the pointer to travel from the trigger into the popup.
            closePopupAndCommit(HOVER_ACTION_POPUP_CLOSE_DELAY);
        }
    };

    useEffect(() => {
        const close = () => closePopupAndCommit();
        closeSelfRef.current = close;
        hoverActionPopupClosers.add(close);
        return () => {
            hoverActionPopupClosers.delete(close);
            clearTimeout(showTimer.current);
            cancelScheduledClose();
        };
    }, []);

    useEffect(() => {
        if (!showPopup) return;

        // Use click/touchend (not mousedown) so focused InlineDurationEditor inputs
        // blur first and can auto-save before this popup unmounts.
        const handleOutsideClose = (event) => {
            const target = event.target;
            if (triggerRef.current?.contains(target)) return;
            if (popupContentRef.current?.contains(target)) return;

            closePopupAndCommit();
        };

        const handleScrollClose = (event) => {
            const target = event.target;
            if (target instanceof Node && popupContentRef.current?.contains(target)) {
                return;
            }
            closePopupAndCommit();
        };

        const scrollTargets = getScrollableAncestors(triggerRef.current);

        document.addEventListener("click", handleOutsideClose);
        document.addEventListener("touchend", handleOutsideClose);
        // Only the activity-list scroller(s) that contain this trigger.
        scrollTargets.forEach((el) => {
            el.addEventListener("scroll", handleScrollClose);
        });
        return () => {
            document.removeEventListener("click", handleOutsideClose);
            document.removeEventListener("touchend", handleOutsideClose);
            scrollTargets.forEach((el) => {
                el.removeEventListener("scroll", handleScrollClose);
            });
        };
    }, [showPopup]);

    const hasAction = typeof onAction === "function";
    const hasActionContent = React.isValidElement(actionContent);

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                onClick={handleClick}
                style={{ display: "inline-flex" }}
            >
                <img
                    ref={anchorRef}
                    src={icon}
                    style={{
                      width: 14,
                      height: 14,
                      visibility: hideIcon ? "hidden" : undefined,
                    }}
                />
            </div>

            {showPopup && (
                <Popup
                    anchor={anchorRef.current}
                    show={showPopup}
                    anchorAlign={{ horizontal: "center", vertical: "top" }}
                    popupAlign={{ horizontal: "center", vertical: "bottom" }}
                >
                    <HoverActionPopupCloseContext.Provider value={closePopupImmediately}>
                      <HoverActionPopupInteractionContext.Provider value={popupEnteredRef}>
                        <div
                            ref={popupContentRef}
                            onMouseEnter={handlePopupEnter}
                            onMouseLeave={() => closePopupAndCommit(HOVER_ACTION_POPUP_CLOSE_DELAY)}
                            style={{
                                background: "#000",
                                color: "#fff",
                                pointerEvents: hasAction || hasActionContent ? "auto" : "none",
                                padding: hasActionContent ? "4px 8px 8px" : "8px",
                                borderRadius: 4,
                                maxWidth: hasActionContent ? 280 : 220,
                                backgroundColor: hasActionContent ? "#292929" : "#000",
                                fontSize: hasActionContent ? 12 : 13,
                                lineHeight: hasActionContent ? 1.33 : undefined,
                                letterSpacing: hasActionContent ? 0.5 : undefined,
                            }}
                        >
                            <div>{content}</div>

                        {hasActionContent ? (
                            <div style={{ marginTop: 8 }}>
                                {actionContent}
                            </div>
                        ) : hasAction && (
                            <div
                                className="tooltip-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAction();
                                  if (closeOnAction) closePopupImmediately();
                                }}
                                style={{ marginTop: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
                            >
                                {actionIcon && (
                                    <img src={actionIcon} style={{ width: 16, height: 16 }} />
                                )}
                                <span>{actionLabel}</span>
                            </div>
                        )}
                        </div>
                      </HoverActionPopupInteractionContext.Provider>
                    </HoverActionPopupCloseContext.Provider>
                </Popup>
            )}
        </>
    );
}

/**
 * Return the mapped title if it exists in the mapping, otherwise return the original title.
 * @param {string} title - The original title
 * @param {Object} [mapping={}] - The mapping object
 * @returns {string} The mapped title if it exists, otherwise the original title
 */
export  const getMappedTitle = (title, mapping = {}) => {
  return mapping[title] || title;
}

export const EmailFilterCell = ({
  value,
  onChange,
  options = [],
  defaultKey = null,
  placeholder
}) => {

  // 👉 初始化选中
  const initialKey = defaultKey ?? value ?? "";

  const [selectedKey, setSelectedKey] = useState(initialKey);
  const [opened, setOpened] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [searchKeyword, setSearchKeyword] = useState("");

  const inputRef = useRef(null);

  // 👉 同步外部 value
  useEffect(() => {
    setSelectedKey(value || "");
  }, [value]);

  // 👉 options 更新同步
  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  // 👉 change
  const handleChange = (e) => {
    const newKey = e.value?.key ?? "";

    setSelectedKey(newKey);
    setSearchKeyword("");
    setFilteredOptions(options);

    onChange({
      value: newKey,
      operator: newKey ? "eq" : "",
    });

    setOpened(false);
  };

  // 👉 search filter
  const handleFilterChange = (e) => {
    const filterValue = e.filter?.value || "";
    const normalizedFilterValue = filterValue.toLowerCase();

    setSearchKeyword(filterValue);

    setFilteredOptions(
      options.filter(o =>
        o.label.toLowerCase().includes(normalizedFilterValue)
      )
    );

    setOpened(true);
  };

  const handleBlur = () => setOpened(false);

  // 👉 当前选中项
  const selectedOption =
    options.find(o => o.key === selectedKey) || null;

  const renderHighlightedOption = (li, itemProps) => {
    const dataItem = itemProps.dataItem || {};
    const label = dataItem.label || "";
    return (
      <HighlightText
        number={dataItem.number}
        name={dataItem.name || label}
        highlight={searchKeyword}
        li={li}
        enableTooltip={true}
      />
    );
  };

  return (
    <ComboBox
      className="search-combobox"
      placeholder={placeholder}
      ref={inputRef}
      data={filteredOptions}
      textField="label"
      dataItemKey="key"
      value={selectedOption}
      onChange={handleChange}
      onBlur={handleBlur}
      onOpen={() => setOpened(true)}
      onFocus={() => setOpened(true)}
      opened={opened}
      filterable
      clearButton={true}
      onFilterChange={handleFilterChange}
      itemRender={renderHighlightedOption}
      style={{ width: "100%" }}
    />
  );
};

export const getSlotDurationSeconds = (slot) => {
    if (!slot) return null;

    if (slot.min_working_applied_duration) {
        return slot.min_working_applied_duration;
    }

    if (slot.start && slot.stop) {
        const start = moment(slot.start);
        const end = moment(slot.stop);

        if (!start.isValid() || !end.isValid()) return null;

        return end.diff(start, 'seconds');
    }
    if (slot.is_open) {
      return slot.duration * 3600;
    }

    return null;
};
